import React, {
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useCallback
} from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription
} from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Building2,
  MapPin,
  Phone,
  Clock,
  Settings,
  Tag,
  FileText,
  Briefcase,
  Globe,
  Link2,
  Users,
  Calendar,
  Star,
  MessageSquare,
  Loader2,
  Check,
  X
} from 'lucide-react';
import InfoField from './InfoField.js';
import PhoneField from './PhoneField.js';
import OpeningDateField from './OpeningDateField.js';
import Categories from './Categories.js';
import Hours from './Hours.js';
import ServiceArea from './ServiceArea.js';
import ProfileOptimizationRating from './ProfileOptimizationRating.js';
import { getGoogleProfileInfo } from './services/index';
import { selectedClientSelector } from '../../store/slices/selectedClient';
import {
  API_UPDATE_GOOGLE_PROFILE_INFO,
  API_GET_CATEGORIES,
  API_ENDPOINT_QUALITY_AGENT,
  API_ENDPOINT_FIX_FIELD,
  API_ENDPOINT_DISMISS_FIELD
} from '../../../src/utils';
import SpecialHours from './SpecialHours';
import './styles.css';
import QualityAgentNotification from './QualityAgentNotification';

// SUGERENCIAS MOCKEADAS ALEATORIAS - Movido fuera del componente para evitar recreación
const agentSuggestions = {
  serviceArea: [
    'Add more cities to expand your coverage.',
    'Include nearby metropolitan areas.',
    'Check that all service areas are correctly spelled.'
  ],
  reviewFrequency: [
    'Ask your clients to leave a review after each service.',
    'Send periodic reminders to get more reviews.'
  ],
  googleRating: [
    'Ask satisfied clients to leave a positive review.',
    'Respond to negative reviews to improve your reputation.'
  ],
  numberOfQAs: [
    'Add relevant FAQs for your clients.',
    'Answer the most common questions on your profile.'
  ],
  answeredAllQAs: [
    'Answer all questions to build trust.',
    'Make sure there are no unanswered questions.'
  ],
  reviewReplies: [
    'Reply to all reviews to show engagement.',
    'Thank your clients for their feedback.'
  ],
  numberOfImages: [
    'Add more high-quality images to attract clients.',
    'Include photos of your products and team.'
  ],
  postFrequency: [
    'Post at least once a week.',
    'Maintain a consistent posting schedule.'
  ],
  postKeywordOptimization: [
    'Include relevant keywords in your posts.',
    'Optimize titles and descriptions with popular terms.'
  ]
};

const GoogleBusinessProfile = forwardRef(
  (
    {
      isEditing,
      setIsEditing,
      showQualityAgent,
      setShowQualityAgent,
      loadingAgent,
      setLoadingAgent,
      handleShowQualityAgentRef,
      onOptimizationChange,
      onCriteriaUpdate,
      onChangesUpdate,
      currSection,
      setCurrSection,
      hasProPlan
    },
    ref
  ) => {
    // TODOS LOS HOOKS DEBEN ESTAR AL PRINCIPIO
    const [googleProfileInfo, setGoogleProfileInfo] = useState({
      loading: true,
      data: {}
    });
    const [editedFields, setEditedFields] = useState({});
    const [categories, setCategories] = useState([]);
    const [gettingCategories, setGettingCategories] = useState(false);
    const [hours, setHours] = useState([]);
    const [fixedDescription, setFixedDescription] = useState(null);
    // loadingAgent ahora viene como prop desde el componente padre
    const [qualityAgentData, setQualityAgentData] = useState(null);
    const [randomAgentSuggestions, setRandomAgentSuggestions] = useState({});
    const [fixedServiceArea, setFixedServiceArea] = useState(null);
    const [forceRender, setForceRender] = useState(0);
    const [addedRecommendedServices, setAddedRecommendedServices] = useState(
      new Set()
    );
    const [fixedServicesList, setFixedServicesList] = useState(null);
    // Estados para campos corregidos por el quality agent
    const [fixedPostsKeywords, setFixedPostsKeywords] = useState(null);
    const [recommendedServices, setRecommendedServices] = useState(null);
    const [recommendedCategories, setRecommendedCategories] = useState(null);
    const [recommendedServiceAreas, setRecommendedServiceAreas] =
      useState(null);
    const [addedRecommendedServiceAreas, setAddedRecommendedServiceAreas] =
      useState(new Set());

    const [loadingFix, setLoadingFix] = useState({});
    const [loadingDismiss, setLoadingDismiss] = useState({});

    const [dismissedNotifications, setDismissedNotifications] = useState({});

    const formatMoreHours = useCallback((moreHours) => {
      if (!moreHours) {
        return '—';
      }

      if (typeof moreHours === 'string') {
        return moreHours;
      }

      const formatDay = (day) => {
        if (!day || typeof day !== 'string') {
          return 'Unknown day';
        }

        return day.charAt(0).toUpperCase() + day.toLowerCase().slice(1);
      };

      const formatTime = (time) => {
        if (!time || typeof time !== 'object') {
          return '--:--';
        }

        const hours = String(time.hours ?? 0).padStart(2, '0');
        const minutes = String(time.minutes ?? 0).padStart(2, '0');
        return `${hours}:${minutes}`;
      };

      const formatPeriods = (periods) => {
        if (!Array.isArray(periods) || periods.length === 0) {
          return '';
        }

        return periods
          .map((period) => {
            if (!period || typeof period !== 'object') {
              return null;
            }

            const day = formatDay(period.openDay);
            const open = formatTime(period.openTime);
            const close = formatTime(period.closeTime);

            return `${day} ${open} - ${close}`;
          })
          .filter(Boolean)
          .join(', ');
      };

      if (Array.isArray(moreHours)) {
        const lines = moreHours
          .map((entry) => {
            if (typeof entry === 'string') {
              return entry;
            }

            if (entry && typeof entry === 'object') {
              const typeLabel =
                typeof entry.hoursTypeId === 'string'
                  ? entry.hoursTypeId.replace(/_/g, ' ').trim()
                  : 'Hours';
              const formattedPeriods = formatPeriods(entry.periods);

              return formattedPeriods
                ? `${typeLabel}: ${formattedPeriods}`
                : typeLabel;
            }

            return null;
          })
          .filter(Boolean);

        return lines.length > 0 ? lines.join(' | ') : '—';
      }

      if (typeof moreHours === 'object') {
        const typeLabel =
          typeof moreHours.hoursTypeId === 'string'
            ? moreHours.hoursTypeId.replace(/_/g, ' ').trim()
            : '';
        const formattedPeriods = formatPeriods(moreHours.periods);

        if (typeLabel && formattedPeriods) {
          return `${typeLabel}: ${formattedPeriods}`;
        }

        if (formattedPeriods) {
          return formattedPeriods;
        }

        if (typeLabel) {
          return typeLabel;
        }
      }

      return '—';
    }, []);

    // Función helper para verificar si hay alguna operación en progreso
    const isAnyOperationInProgress = useCallback(() => {
      return (
        Object.values(loadingFix).some((loading) => loading === true) ||
        Object.values(loadingDismiss).some((loading) => loading === true)
      );
    }, [loadingFix, loadingDismiss]);

    const [saveMessage, setSaveMessage] = useState({
      show: false,
      type: '',
      message: ''
    });

    const { selectedClient } = useSelector(selectedClientSelector);

    // Only allow editing if user has pro plan and isEditing is true
    const isEditingAllowed = isEditing && hasProPlan;

    useEffect(() => {}, [dismissedNotifications]);

    // Definir getCategories antes de usarla en useEffect
    const getCategories = useCallback(async () => {
      // Validar que la URL de la API existe
      if (!API_GET_CATEGORIES) {
        console.error(
          'API_GET_CATEGORIES is not defined in environment variables'
        );
        setGettingCategories(false);
        return [];
      }

      setGettingCategories(true);
      const equalsCheck = (a, b) => {
        return JSON.stringify(a) === JSON.stringify(b);
      };
      try {
        await axios.get(API_GET_CATEGORIES).then((response) => {
          const { categories: cs } = response.data;
          if (
            !equalsCheck(
              categories,
              cs.map((category) => ({
                ...category,
                value: category.id,
                label: category.display_name
              }))
            ) ||
            googleProfileInfo.data?.categories?.primaryCategory?.displayName ===
              ''
          ) {
            setCategories(
              cs.map((category) => ({
                ...category,
                value: category.id,
                label: category.display_name
              }))
            );
          }
          setGettingCategories(false);
        });
      } catch (e) {
        console.error(
          'An error has ocurred retrieving the google categories',
          e
        );
        setGettingCategories(false);
        return [];
      }
      setGettingCategories(false);
    }, [categories]);

    // useEffect hooks
    useEffect(() => {
      if (categories.length === 0 && !gettingCategories) {
        getCategories();
      }
    }, [categories.length, gettingCategories, getCategories]);

    useEffect(() => {
      if (googleProfileInfo.data?.regularHours?.periods) {
        const completeHours = (periods) => {
          const defaultOpenTime = { hours: 8 };
          const defaultCloseTime = { hours: 17 };
          const defaultDayOrder = [
            'MONDAY',
            'TUESDAY',
            'WEDNESDAY',
            'THURSDAY',
            'FRIDAY',
            'SATURDAY',
            'SUNDAY'
          ];

          return periods.map((period, index) => {
            return (
              period || {
                openDay: defaultDayOrder[index],
                openTime: defaultOpenTime,
                closeDay: defaultDayOrder[index],
                closeTime: defaultCloseTime
              }
            );
          });
        };

        const completedHours = completeHours(
          googleProfileInfo.data.regularHours.periods
        );

        if (JSON.stringify(completedHours) !== JSON.stringify(hours)) {
          setHours(completedHours);
        }
      }
    }, [googleProfileInfo.data?.regularHours?.periods, hours]);

    // Definir loadGoogleProfile antes de usarla en useEffect
    const loadGoogleProfile = useCallback(async () => {
      setGoogleProfileInfo({ loading: true, data: null });
      if (selectedClient?.id) {
        try {
          const profileInfo = await getGoogleProfileInfo(selectedClient.id);
          setGoogleProfileInfo(profileInfo);
        } catch (error) {
          console.error('Error loading Google profile info:', error);
          setGoogleProfileInfo({ loading: false, data: null });
        }
      } else {
        setGoogleProfileInfo({ loading: false, data: null });
      }
    }, [selectedClient?.id]);

    useEffect(() => {
      const card = document.getElementById('scrollCard');
      if (card) {
        const handleScroll = () => {
          const sections = card.querySelectorAll('div[id]');
          let newSectionId = '';

          sections.forEach((section) => {
            const rect = section.getBoundingClientRect();
            if (rect.top >= 0 && rect.top <= card.clientHeight) {
              newSectionId = section.id;
            }
          });

          if (newSectionId && newSectionId !== currSection) {
            setCurrSection(newSectionId);
          }
        };

        card.addEventListener('scroll', handleScroll);
        return () => {
          card.removeEventListener('scroll', handleScroll);
        };
      }
    }, [currSection]);

    useEffect(() => {
      loadGoogleProfile();

      return () => {
        setGoogleProfileInfo({}); // Limpiar estado si es necesario
      };
    }, [selectedClient?.id, loadGoogleProfile]);

    // Reiniciar estado de notificaciones cuando cambie el cliente
    useEffect(() => {
      setDismissedNotifications({});
      setShowQualityAgent(false);
      setQualityAgentData(null);
      setFixedDescription(null);
      setFixedServiceArea(null);
      setForceRender(0);
      setFixedServicesList(null);
      // Limpiar estados de campos corregidos
      setFixedPostsKeywords(null);
      setRecommendedServices(null);
      setRecommendedServiceAreas(null);
      setAddedRecommendedServiceAreas(new Set());
      setRandomAgentSuggestions({});
    }, [selectedClient?.id]);

    // useEffect para monitorear cambios en fixedServiceArea
    // useEffect(() => {
    //   if (fixedServiceArea !== null) {
    //     setForceRender((prev) => prev + 1);
    //   }
    // }, [fixedServiceArea]);

    // useEffect para procesar recomendaciones cuando qualityAgentData cambie
    useEffect(() => {
      // Procesar servicios recomendados
      if (
        qualityAgentData?.recomendations?.serviceItems?.recommendedServices
          ?.message
      ) {
        try {
          const recommendedServicesList = parseRecommendedServices(
            qualityAgentData.recomendations.serviceItems.recommendedServices
              .message
          );
          setRecommendedServices(recommendedServicesList);
        } catch (error) {
          console.error('Error parsing recommended services:', error);
        }
      }

      // Procesar categorías recomendadas
      if (
        qualityAgentData?.recomendations?.categories?.additionalCategories
          ?.message
      ) {
        try {
          const recommendedCategoriesList = JSON.parse(
            qualityAgentData.recomendations.categories.additionalCategories
              .message
          );

          // Función helper para normalizar IDs de categorías
          const normalizeCategoryId = (id) => {
            if (!id) return '';
            // Remover el prefijo "categories/" si existe
            return id.replace(/^categories\//, '').toLowerCase();
          };

          // Obtener todas las categorías existentes (primary + additional)
          const primaryCategory =
            googleProfileInfo.data?.categories?.primaryCategory;
          const currentAdditionalCategories =
            googleProfileInfo.data?.categories?.additionalCategories || [];

          // Crear un Set con todos los IDs de categorías existentes (normalizados)
          const existingCategoryIds = new Set();

          // Agregar primary category
          if (primaryCategory?.name) {
            existingCategoryIds.add(normalizeCategoryId(primaryCategory.name));
          }

          // Agregar additional categories
          currentAdditionalCategories.forEach((cat) => {
            const catId = cat.name || cat.id;
            if (catId) {
              existingCategoryIds.add(normalizeCategoryId(catId));
            }
          });

          // Filtrar las categorías recomendadas que ya existen
          const filteredCategories = recommendedCategoriesList.filter(
            (recCat) => {
              const categoryId = normalizeCategoryId(recCat.name || recCat.id);
              const isExisting = existingCategoryIds.has(categoryId);

              if (isExisting) {
              }

              return !isExisting;
            }
          );

          setRecommendedCategories(filteredCategories);
        } catch (error) {
          console.error('Error parsing recommended categories:', error);
        }
      }

      // Procesar service areas recomendadas
      if (
        qualityAgentData?.recomendations?.serviceArea?.places?.placeInfos ||
        qualityAgentData?.recomendations?.serviceArea?.places?.message
      ) {
        try {
          const apiPlaceInfos =
            qualityAgentData.recomendations?.serviceArea?.places?.placeInfos;
          const messageText =
            qualityAgentData.recomendations?.serviceArea?.places?.message;

          let parsedServiceAreas = [];
          if (apiPlaceInfos && apiPlaceInfos.length > 0) {
            parsedServiceAreas = apiPlaceInfos.map((place, index) => ({
              id: `place_${index}`,
              placeName: place.placeName || place.name,
              placeId: place.placeId || null
            }));
          } else if (messageText && typeof messageText === 'string') {
            // Ignorar mensajes de estado del backend (no son recomendaciones)
            const isStatusMessage =
              messageText.toLowerCase().includes('already has') ||
              messageText.toLowerCase().includes('maximum') ||
              messageText.toLowerCase().includes('complete');

            if (!isStatusMessage) {
              parsedServiceAreas = messageText
                .split(',')
                .map((s) => s.trim())
                .filter((s) => s.length > 0)
                .map((placeName, index) => ({
                  id: `place_${index}`,
                  placeName,
                  placeId: null
                }));
            }
          }

          // Filtrar las áreas que ya existen en el perfil actual
          // para evitar duplicación visual después del refresh
          const currentServiceAreas =
            googleProfileInfo.data?.serviceArea?.places?.placeInfos || [];

          const existingPlaceNames = new Set(
            currentServiceAreas.map((p) =>
              (p.placeName || '').toLowerCase().trim()
            )
          );

          const filteredServiceAreas = parsedServiceAreas.filter((place) => {
            const normalizedName = (place.placeName || '').toLowerCase().trim();
            return !existingPlaceNames.has(normalizedName);
          });

          setRecommendedServiceAreas(filteredServiceAreas);
        } catch (error) {
          console.error('Error parsing recommended service areas:', error);
        }
      } else {
        // Si no hay recomendaciones del backend, limpiar el estado
        setRecommendedServiceAreas(null);
      }
    }, [qualityAgentData, googleProfileInfo.data]);

    // useEffect vacío removido - causaba error de referencia

    useImperativeHandle(handleShowQualityAgentRef, () => ({
      handleShowQualityAgent,
      cancelEdit: handleCancel,
      saveChanges: handleSave
    }));

    // FUNCIONES Y LÓGICA DESPUÉS DE LOS HOOKS

    const [GBPfields] = useState({
      businessName: false,
      businessCategory: false,
      description: false,
      openingDate: true, // Desactivado - no editable
      phoneNumber: false,
      website: false,
      socialProfiles: true,
      shortName: true,
      businessLocation: true, // to review
      serviceArea: false, // ahora editable
      hours: true, // to review
      specialHours: true,
      moreHours: true,
      // NUEVOS CAMPOS
      reviewFrequency: true,
      googleRating: true,
      numberOfQAs: true,
      answeredAllQAs: true,
      servicesList: false,
      reviewReplies: true,
      numberOfImages: true,
      postFrequency: true,
      postKeywordOptimization: false
    });

    // MOCK de sugerencias del agente para los nuevos campos
    const qualityAgentMock = {
      serviceArea: {
        status: 'bad',
        message: 'The service area is too limited.',
        suggestion: 'Add more cities to expand your coverage.'
      },
      reviewFrequency: {
        status: 'good',
        message: 'The review frequency is OK.'
      },
      googleRating: {
        status: 'bad',
        message: 'The Google rating is low.',
        suggestion: 'Ask satisfied clients to leave a positive review.'
      },
      numberOfQAs: {
        status: 'good',
        message: 'The number of Q&As is OK.'
      },
      answeredAllQAs: {
        status: 'bad',
        message: 'Not all questions have been answered.',
        suggestion: 'Answer all questions to build trust.'
      },
      reviewReplies: {
        status: 'good',
        message: 'You reply to reviews OK.'
      },
      numberOfImages: {
        status: 'bad',
        message: 'There are few images on the profile.',
        suggestion: 'Add more high-quality images to attract clients.'
      },
      postFrequency: {
        status: 'good',
        message: 'The posting frequency is OK.'
      },
      postKeywordOptimization: {
        status: 'bad',
        message: 'Posts are not optimized with keywords.',
        suggestion: 'Include relevant keywords in your posts.'
      }
    };

    const handleCancel = useCallback(() => {
      setIsEditing(false);
      setEditedFields({});

      // Recargar los datos originales para descartar cualquier cambio
      loadGoogleProfile();
    }, [loadGoogleProfile]);

    const handleSave = () => {
      // Verificar si hay cambios antes de guardar
      if (Object.keys(editedFields).length === 0) {
        return;
      }

      setIsEditing(false);

      // Construir solo los campos que han sido modificados
      const editedData = {
        clientId: selectedClient?.id
      };

      // Solo incluir campos que realmente han sido modificados
      if (editedFields.businessName !== undefined) {
        editedData.title = editedFields.businessName;
      }

      if (editedFields.businessCategory !== undefined) {
        editedData.categories = editedFields.businessCategory;
      }

      if (editedFields.description !== undefined) {
        editedData.profile = {
          description: editedFields.description
        };
      }

      if (editedFields.website !== undefined) {
        editedData.websiteUri = editedFields.website;
      }

      if (editedFields.openingDate !== undefined) {
        editedData.openInfo = {
          status: 'OPEN',
          canReopen: true,
          openingDate: editedFields.openingDate
        };
      }

      // Manejar campos personalizados que no son parte de la API estándar de Google
      if (editedFields.postKeywordOptimization !== undefined) {
        // Este campo se almacenará en DynamoDB pero no se enviará a Google Business API
        editedData.posts_info = {
          posts_keywords: editedFields.postKeywordOptimization
        };
      }

      // Agregar otros campos personalizados si han sido modificados
      if (editedFields.reviewFrequency !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          reviewFrecuency: editedFields.reviewFrequency
        };
      }

      if (editedFields.googleRating !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          averageRating: editedFields.googleRating
        };
      }

      if (editedFields.reviewReplies !== undefined) {
        editedData.reviews_info = {
          ...editedData.reviews_info,
          reviewsReplied: editedFields.reviewReplies
        };
      }

      if (editedFields.numberOfImages !== undefined) {
        editedData.images_info = {
          ...editedData.images_info,
          totalMediaItemCount: editedFields.numberOfImages
        };
      }

      if (editedFields.postFrequency !== undefined) {
        editedData.posts_info = {
          ...editedData.posts_info,
          posts_frecuency: editedFields.postFrequency
        };
      }

      // Separar campos estándar de Google de campos personalizados
      const googleStandardFields = {};
      const customFields = {};

      // Campos que van a Google Business API
      if (editedData.title) googleStandardFields.title = editedData.title;
      if (editedData.categories)
        googleStandardFields.categories = editedData.categories;
      if (editedData.profile) googleStandardFields.profile = editedData.profile;
      if (editedData.websiteUri)
        googleStandardFields.websiteUri = editedData.websiteUri;
      if (editedData.openInfo)
        googleStandardFields.openInfo = editedData.openInfo;

      // Campos personalizados que solo van a DynamoDB
      if (editedData.posts_info)
        customFields.posts_info = editedData.posts_info;
      if (editedData.reviews_info)
        customFields.reviews_info = editedData.reviews_info;
      if (editedData.images_info)
        customFields.images_info = editedData.images_info;

      // Agregar campos personalizados al objeto principal
      const finalData = {
        ...googleStandardFields,
        ...customFields,
        clientId: editedData.clientId
      };

      const headers = {
        'Content-Type': 'application/json'
      };

      axios
        .put(API_UPDATE_GOOGLE_PROFILE_INFO, finalData, { headers })
        .then((response) => {
          // Limpiar campos editados después de guardar exitosamente
          setEditedFields({});

          // Recargar los datos del perfil para mostrar los cambios
          loadGoogleProfile();
        })
        .catch((error) => {
          console.error('An error has ocurred saving the data:', error);
          console.error('Error response:', error.response?.data);

          // En caso de error, mantener el modo de edición
          setIsEditing(true);

          // Determinar el mensaje de error apropiado
          let errorMessage = error.response?.data?.message || error.message;

          // Si el error es sobre access_token, mostrar un mensaje más claro
          if (
            errorMessage.includes('access_token') ||
            errorMessage.includes('Missing GBP credentials')
          ) {
            errorMessage =
              'Your Google Business Profile is not connected. Please connect your Google Business Profile account first in the Links section.';
          }

          // Mostrar mensaje de error
          setSaveMessage({
            show: true,
            type: 'error',
            message: `Error saving changes: ${errorMessage}`
          });

          // Ocultar mensaje después de 8 segundos
          setTimeout(() => {
            setSaveMessage({ show: false, type: '', message: '' });
          }, 8000);
        });
    };

    const handleFieldChange = (fieldName, value) => {
      setEditedFields((prevEditedFields) => {
        if (prevEditedFields[fieldName] === value) {
          return prevEditedFields;
        }

        const newEditedFields = {
          ...prevEditedFields,
          [fieldName]: value
        };

        return newEditedFields;
      });
    };

    // Lógica simple de calidad para la descripción
    const description = googleProfileInfo.data?.profile?.description || '';
    const minLength = 30;
    // El valor visual y de análisis debe ser el fixedDescription si existe
    const visualDescription =
      fixedDescription !== null
        ? fixedDescription
        : googleProfileInfo.data?.profile?.description || '';

    const visualPostsKeywords =
      fixedPostsKeywords !== null
        ? fixedPostsKeywords
        : googleProfileInfo.data?.posts_info?.posts_keywords || '';

    // Handler para el botón Quality Agent: llama al endpoint y muestra el cuadro con la sugerencia real
    const handleShowQualityAgent = useCallback(async () => {
      setLoadingAgent(true);
      setShowQualityAgent(true);
      setQualityAgentData(null);
      setDismissedNotifications({});
      setLoadingFix({});
      setLoadingDismiss({});
      // Elegir sugerencias aleatorias para los campos que NO están en el endpoint
      const randoms = {};
      Object.keys(agentSuggestions).forEach((key) => {
        if (
          ['postKeywordOptimization', 'serviceArea', 'servicesList'].includes(
            key
          )
        ) {
          const arr = agentSuggestions[key];
          randoms[key] = arr[Math.floor(Math.random() * arr.length)];
        }
      });
      setRandomAgentSuggestions(randoms);

      if (!selectedClient?.id) {
        console.error('No client ID available');
        setLoadingAgent(false);
        return;
      }

      try {
        const { data } = await axios.post(
          API_ENDPOINT_QUALITY_AGENT,
          { clientId: selectedClient?.id },
          { headers: { 'Content-Type': 'application/json' } }
        );
        setQualityAgentData(data);
      } catch (e) {
        console.error('API Error:', e);
        setQualityAgentData(null);
      } finally {
        setLoadingAgent(false);
      }
    }, [selectedClient?.id, setLoadingAgent, setShowQualityAgent]);

    // Usar los datos del endpoint para la notificación de descripción y otros campos
    let isDescriptionGood = false;
    let message = '';
    let suggestion = '';
    // Nuevas sugerencias del endpoint
    let postsKeywordsSuggestion = '';
    let postsFrecuencySuggestion = '';
    let reviewsRepliedSuggestion = '';
    let reviewFrecuencySuggestion = '';
    let totalReviewCountSuggestion = '';
    let averageRatingSuggestion = '';
    let totalMediaItemCountSuggestion = '';
    if (qualityAgentData) {
      // Descripción
      isDescriptionGood = qualityAgentData.status === 'good';
      message = qualityAgentData.messages?.[qualityAgentData.status] || '';
      suggestion =
        qualityAgentData?.recomendations?.profile?.description?.message || '';
      // Posts
      postsKeywordsSuggestion =
        qualityAgentData.recomendations?.posts_info?.posts_keywords?.message ||
        '';
      postsFrecuencySuggestion =
        qualityAgentData.recomendations?.posts_info?.posts_frecuency?.message ||
        '';
      // Reviews
      reviewsRepliedSuggestion =
        qualityAgentData.recomendations?.review_info?.reviewsReplied?.message ||
        '';
      reviewFrecuencySuggestion =
        qualityAgentData.recomendations?.review_info?.reviewFrecuency
          ?.message || '';
      totalReviewCountSuggestion =
        qualityAgentData.recomendations?.review_info?.totalReviewCount
          ?.message || '';
      averageRatingSuggestion =
        qualityAgentData.recomendations?.review_info?.averageRating?.message ||
        '';
      // Imágenes
      totalMediaItemCountSuggestion =
        qualityAgentData.recomendations?.images_info?.totalMediaItemCount
          ?.message || '';
    }

    // Función para manejar la aplicación de una categoría individual
    const handleFixIndividualCategory = async (category) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        return;
      }

      const categoryKey = `category_${category.id}`;
      setLoadingFix((prev) => ({ ...prev, [categoryKey]: true }));

      try {
        // Crear un payload con solo la categoría individual
        const payload = {
          clientId: selectedClient?.id,
          fieldPath: 'recomendations.categories.additionalCategories',
          selectedCategories: [category]
        };

        const response = await axios.post(API_ENDPOINT_FIX_FIELD, payload);

        // Actualizar el estado local de googleProfileInfo para agregar la nueva categoría
        setGoogleProfileInfo((prev) => {
          if (!prev.data) return prev;

          const currentAdditionalCategories =
            prev.data.categories?.additionalCategories || [];

          // Crear el objeto de categoría en el formato correcto
          const newCategory = {
            name: category.name || category.id,
            displayName: category.display_name,
            serviceTypes: category.serviceTypes || []
          };

          return {
            ...prev,
            data: {
              ...prev.data,
              categories: {
                ...prev.data.categories,
                additionalCategories: [
                  ...currentAdditionalCategories,
                  newCategory
                ]
              }
            }
          };
        });

        // Mostrar mensaje de éxito
        setSaveMessage({
          type: 'success',
          text: `Successfully added "${category.display_name}" to your business profile.`
        });

        // No es necesario filtrar manualmente recommendedCategories aquí
        // El useEffect que observa googleProfileInfo.data se encargará de filtrar
        // las categorías que ya están agregadas automáticamente
      } catch (error) {
        console.error('Error fixing individual category:', error);
        setSaveMessage({
          type: 'error',
          text: `Failed to add "${category.display_name}". Please try again.`
        });
      } finally {
        setLoadingFix((prev) => ({ ...prev, [categoryKey]: false }));
      }
    };

    // Función para descartar una categoría individual
    const handleDismissIndividualCategory = async (categoryId) => {
      const categoryKey = `category_${categoryId}`;
      setLoadingDismiss((prev) => ({ ...prev, [categoryKey]: true }));

      try {
        // Simular delay para la animación
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Remover la categoría de la lista de recomendadas
        setRecommendedCategories((prev) =>
          prev.filter((category) => category.id !== categoryId)
        );
      } catch (error) {
        console.error('Error dismissing individual category:', error);
      } finally {
        setLoadingDismiss((prev) => ({ ...prev, [categoryKey]: false }));
      }
    };

    // Función para manejar el fix de cualquier campo llamando al endpoint
    const handleFixField = async (fieldPath, additionalPayload = {}) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        return;
      }

      // Para el caso especial de profile.description, usar 'description' como clave
      let loadingKey;
      let notificationKey;

      if (fieldPath === 'profile.description') {
        loadingKey = 'description';
        notificationKey = 'description';
      } else if (fieldPath === 'posts_info.posts_keywords') {
        loadingKey = 'postKeywordOptimization';
        notificationKey = 'postKeywordOptimization';
      } else if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
        loadingKey = 'services_general';
        notificationKey = 'services_general';
      } else if (fieldPath === 'serviceItems.recommendedServices') {
        loadingKey = 'recommended_services_general';
        notificationKey = 'recommended_services_general';
      } else if (fieldPath.startsWith('serviceItems.recommendedServices.')) {
        // Para servicios recomendados individuales
        const serviceId = additionalPayload?.serviceId || 'service';
        loadingKey = `recommended_service_${serviceId}`;
        notificationKey = `recommended_service_${serviceId}`;
      } else if (fieldPath === 'categories.additionalCategories') {
        loadingKey = 'recommended_categories';
        notificationKey = 'recommended_categories';
      } else if (fieldPath.startsWith('serviceArea.places.placeInfos')) {
        // Para service areas individuales
        const placeId = additionalPayload?.placeId || 'place';
        loadingKey = `recommended_service_area_${placeId}`;
        notificationKey = `recommended_service_area_${placeId}`;
      } else if (
        fieldPath.startsWith('serviceItems.') &&
        fieldPath.endsWith('.description')
      ) {
        // Para servicios individuales: serviceItems.{index}.description
        const serviceName = additionalPayload?.serviceName || 'service';
        loadingKey = `service_${serviceName}_individual`;
        notificationKey = `service_${serviceName}_individual`;
      } else {
        // Extraer la clave base del fieldPath (antes del primer punto)
        loadingKey = fieldPath.split('.')[0];
        notificationKey = fieldPath.split('.')[0];
      }

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.${fieldPath}`,
          ...additionalPayload
        });

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
            // Para el fix general de servicios, cerrar también todas las notificaciones individuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            if (currentServices) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[notificationKey] = true;

              // Cerrar todas las notificaciones individuales de servicios
              currentServices.forEach((service) => {
                dismissUpdates[`service_${service.name}_bad`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({
                ...prev,
                [notificationKey]: true
              }));
            }
          } else {
            // Para otros campos, comportamiento normal
            setDismissedNotifications((prev) => ({
              ...prev,
              [notificationKey]: true
            }));
          }

          // Actualizar los valores de los campos con las recomendaciones del quality agent
          if (fieldPath === 'profile.description') {
            setFixedDescription(
              response.data.description ||
                qualityAgentData?.recomendations?.profile?.description
                  ?.message ||
                ''
            );
          } else if (fieldPath === 'posts_info.posts_keywords') {
            setFixedPostsKeywords(
              response.data.postsKeywords ||
                qualityAgentData?.recomendations?.posts_info?.posts_keywords
                  ?.message ||
                ''
            );
          } else if (fieldPath === 'serviceItems.descriptionKeywordAnalysis') {
            // Obtener los servicios actuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            if (
              currentServices &&
              qualityAgentData?.recomendations?.serviceItems
                ?.descriptionKeywordAnalysis?.message
            ) {
              try {
                const rawMessage =
                  qualityAgentData.recomendations.serviceItems
                    .descriptionKeywordAnalysis.message;
                const parsedMessage = parseServiceRecommendations(rawMessage);

                // Crear una nueva lista de servicios con las descripciones actualizadas
                const updatedServices = currentServices.map((service) => {
                  // Buscar la recomendación para este servicio
                  const serviceRecommendation = parsedMessage.find(
                    (item) =>
                      item.freeFormServiceItem?.label?.displayName ===
                        service.name ||
                      item.structuredServiceItem?.label?.displayName ===
                        service.name
                  );

                  // Si el servicio no tiene descripción y hay una recomendación, usar la recomendación
                  const freeFormDescription =
                    serviceRecommendation?.freeFormServiceItem?.label
                      ?.description;
                  const structuredDescription =
                    serviceRecommendation?.structuredServiceItem?.label
                      ?.description;

                  if (
                    (!service.description ||
                      service.description.trim() === '') &&
                    (freeFormDescription || structuredDescription)
                  ) {
                    const descriptionToUse =
                      freeFormDescription || structuredDescription;
                    const recommendationType = freeFormDescription
                      ? 'freeForm'
                      : 'structured';

                    return {
                      ...service,
                      description: descriptionToUse
                    };
                  }

                  return service;
                });

                // Actualizar el estado con los servicios que tienen descripciones
                setFixedServicesList(updatedServices);
              } catch (error) {
                console.error('Error parsing service recommendations:', error);
              }
            } else {
            }
          } else if (fieldPath.startsWith('serviceArea.places.placeInfos')) {
            // Fix de un service area individual
            const { placeId, placeName } = additionalPayload;

            if (placeName) {
              // El backend maneja la actualización, aquí solo cerramos la notificación
              const notificationKey = `recommended_service_area_${placeId}`;
              setDismissedNotifications((prev) => ({
                ...prev,
                [notificationKey]: true
              }));
            } else {
            }
          } else if (fieldPath === 'serviceItems.recommendedServices') {
            // Manejar el fix de servicios recomendados

            // Cerrar la notificación general de servicios recomendados
            setDismissedNotifications((prev) => ({
              ...prev,
              recommended_services_general: true
            }));

            // Cerrar todas las notificaciones individuales de servicios recomendados
            if (recommendedServices && recommendedServices.length > 0) {
              const dismissUpdates = {};
              recommendedServices.forEach((service) => {
                dismissUpdates[`recommended_service_${service.id}`] = true;
              });
              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            }

            // Aquí podrías agregar lógica para agregar los servicios recomendados
            // a la lista de servicios existentes del perfil
          } else if (
            fieldPath.startsWith('serviceItems.recommendedServices.')
          ) {
            // Fix de un servicio recomendado individual
            const { serviceId } = additionalPayload;

            // Buscar el servicio recomendado en la lista
            if (recommendedServices) {
              const serviceToAdd = recommendedServices.find(
                (s) => s.id === serviceId
              );
              if (serviceToAdd) {
                // Actualizar fixedServicesList agregando el nuevo servicio
                setFixedServicesList((prevServices) => {
                  // Si no hay servicios fijos previos, inicializar con los servicios actuales
                  let currentServicesList = prevServices;
                  if (!currentServicesList) {
                    const servicesData = findServicesField(
                      googleProfileInfo.data
                    );
                    currentServicesList =
                      processServicesList(servicesData) || [];
                  }

                  // Verificar que el servicio no exista ya en la lista
                  const alreadyExists = currentServicesList.some(
                    (existingService) =>
                      existingService.name === serviceToAdd.name
                  );

                  if (alreadyExists) {
                    return currentServicesList;
                  }

                  // Agregar el nuevo servicio a la lista (sin el flag isRecommended para que aparezca como normal)
                  const newService = {
                    name: serviceToAdd.name,
                    description: serviceToAdd.description,
                    category: serviceToAdd.category,
                    isAdded: true // Flag para mostrar badge "Added"
                  };

                  return [...currentServicesList, newService];
                });
              }
            }
          } else if (
            fieldPath.startsWith('serviceItems.') &&
            fieldPath.endsWith('.description')
          ) {
            // Manejar el fix de un servicio individual
            const serviceName = additionalPayload?.serviceName;
            const serviceIndex = additionalPayload?.serviceIndex;

            // Obtener la descripción actualizada del response
            let updatedDescription = response.data.description;

            // Si la lambda devuelve la descripción directamente, usarla
            if (!updatedDescription && response.data.updatedService) {
              if (
                response.data.updatedService.structuredServiceItem?.description
              ) {
                updatedDescription =
                  response.data.updatedService.structuredServiceItem
                    .description;
              } else if (
                response.data.updatedService.freeFormServiceItem?.label
                  ?.description
              ) {
                updatedDescription =
                  response.data.updatedService.freeFormServiceItem.label
                    .description;
              }
            }

            // Fallback: buscar en las recomendaciones si no viene en el response
            if (
              !updatedDescription &&
              qualityAgentData?.recomendations?.serviceItems
                ?.descriptionKeywordAnalysis?.message
            ) {
              try {
                const rawMessage =
                  qualityAgentData.recomendations.serviceItems
                    .descriptionKeywordAnalysis.message;
                const parsedMessage = parseServiceRecommendations(rawMessage);

                // Buscar la recomendación específica para este servicio
                const serviceRecommendation = parsedMessage.find(
                  (item) =>
                    item.freeFormServiceItem?.label?.displayName ===
                      serviceName ||
                    item.structuredServiceItem?.label?.displayName ===
                      serviceName
                );

                if (serviceRecommendation) {
                  updatedDescription =
                    serviceRecommendation.freeFormServiceItem?.label
                      ?.description ||
                    serviceRecommendation.structuredServiceItem?.description ||
                    serviceRecommendation.structuredServiceItem?.label
                      ?.description;
                }
              } catch (error) {
                console.error(
                  'Error parsing individual service recommendation:',
                  error
                );
              }
            }

            if (updatedDescription) {
              // Actualizar la lista de servicios con la nueva descripción
              setFixedServicesList((prevServices) => {
                // Si no hay servicios fijos previos, inicializar con los servicios actuales
                let currentServicesList = prevServices;
                if (!currentServicesList) {
                  const servicesData = findServicesField(
                    googleProfileInfo.data
                  );
                  currentServicesList = processServicesList(servicesData) || [];
                }

                return currentServicesList.map((service, index) => {
                  if (index === serviceIndex && service.name === serviceName) {
                    return {
                      ...service,
                      description: updatedDescription
                    };
                  }
                  return service;
                });
              });

              // Cerrar tanto la notificación individual como la general
              // La notificación general se cierra porque el usuario ya empezó a hacer fixes individuales
              setDismissedNotifications((prev) => ({
                ...prev,
                [`service_${serviceName}_bad`]: true,
                services_general: true
              }));
            }
          }
        }
      } catch (error) {
        console.error('Error calling fix field endpoint:', error);
        console.error('Error response:', error.response?.data);
        // Aquí podrías mostrar un mensaje de error al usuario
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de service area individual
    const handleFixRecommendedServiceArea = async (placeId, placeName) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        return;
      }

      const loadingKey = `recommended_service_area_${placeId}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        const fieldPath = `serviceArea.places.placeInfos.${placeId}`;
        await handleFixField(fieldPath, { placeId, placeName });

        // Remover inmediatamente la recomendación de la lista para evitar duplicación visual
        setRecommendedServiceAreas((prev) =>
          prev ? prev.filter((place) => place.id !== placeId) : null
        );

        // Limpiar fixedServiceArea para evitar duplicación con áreas del perfil
        setFixedServiceArea(null);

        // Recargar el perfil para obtener la data actualizada de Google
        await loadGoogleProfile();

        // Limpiar el estado de áreas añadidas
        setAddedRecommendedServiceAreas(new Set());
      } catch (error) {
        console.error('Error adding service area:', error);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Mostrar notificaciones del agente solo cuando showQualityAgent es true Y loadingAgent es false
    const showAgentNotifications = showQualityAgent && !loadingAgent;

    const handleDismissNotification = async (field) => {
      const loadingKey = field;

      if (
        field === 'numberOfImages' ||
        field === 'postFrequency' ||
        field === 'googleRating' ||
        field === 'reviewReplies' ||
        field === 'reviewFrequency' ||
        field === 'postFrequency' ||
        field.startsWith('service_') ||
        field.startsWith('recommended_service_')
      ) {
        setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
        return;
      }
      setLoadingDismiss((prev) => ({ ...prev, [loadingKey]: true }));
      try {
        // Mapear el nombre del campo al fieldPath correcto
        let fieldPath;
        if (field === 'description') {
          fieldPath = 'profile.description';
        } else if (field.startsWith('service_')) {
          const serviceIndex = field.split('_')[1];
          fieldPath = `serviceItems.${serviceIndex}.description`;
        } else if (field.startsWith('recommended_service_')) {
          const serviceId = field.replace('recommended_service_', '');
          fieldPath = `serviceItems.recommendedServices.${serviceId}`;
        } else if (field === 'recommended_services_general') {
          fieldPath = 'serviceItems.recommendedServices';
        } else {
          // Para otros campos, usar el nombre del campo directamente
          fieldPath = field;
        }

        const response = await axios.post(API_ENDPOINT_DISMISS_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.${fieldPath}`
        });

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          if (field === 'services_general') {
            // Si es el dismiss general de servicios, cerrar también todas las notificaciones individuales
            const servicesData = findServicesField(googleProfileInfo.data);
            const currentServices = processServicesList(servicesData);

            if (currentServices) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[field] = true;

              // Cerrar todas las notificaciones individuales de servicios
              currentServices.forEach((service) => {
                dismissUpdates[`service_${service.name}_bad`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
            }
          } else if (field === 'recommended_services_general') {
            // Si es el dismiss general de servicios recomendados, cerrar también todas las notificaciones individuales
            if (recommendedServices && recommendedServices.length > 0) {
              const dismissUpdates = {};

              // Cerrar la notificación general
              dismissUpdates[field] = true;

              // Cerrar todas las notificaciones individuales de servicios recomendados
              recommendedServices.forEach((service) => {
                dismissUpdates[`recommended_service_${service.id}`] = true;
              });

              setDismissedNotifications((prev) => ({
                ...prev,
                ...dismissUpdates
              }));
            } else {
              // Fallback: solo cerrar la notificación general
              setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
            }
          } else {
            // Para otros campos, comportamiento normal
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        }
      } catch (error) {
        console.error('Error calling dismiss field endpoint:', error);
        console.error('Error response:', error.response?.data);
        // Aún cerrar la notificación localmente en caso de error
        if (field === 'services_general') {
          // Si es el dismiss general de servicios, cerrar también todas las notificaciones individuales
          const servicesData = findServicesField(googleProfileInfo.data);
          const currentServices = processServicesList(servicesData);

          if (currentServices) {
            const dismissUpdates = {};

            // Cerrar la notificación general
            dismissUpdates[field] = true;

            // Cerrar todas las notificaciones individuales de servicios
            currentServices.forEach((service) => {
              dismissUpdates[`service_${service.name}_bad`] = true;
            });

            setDismissedNotifications((prev) => ({
              ...prev,
              ...dismissUpdates
            }));
          } else {
            // Fallback: solo cerrar la notificación general
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        } else if (field === 'recommended_services_general') {
          // Si es el dismiss general de servicios recomendados, cerrar también todas las notificaciones individuales
          if (recommendedServices && recommendedServices.length > 0) {
            const dismissUpdates = {};

            // Cerrar la notificación general
            dismissUpdates[field] = true;

            // Cerrar todas las notificaciones individuales de servicios recomendados
            recommendedServices.forEach((service) => {
              dismissUpdates[`recommended_service_${service.id}`] = true;
            });

            setDismissedNotifications((prev) => ({
              ...prev,
              ...dismissUpdates
            }));
          } else {
            // Fallback: solo cerrar la notificación general
            setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
          }
        } else {
          // Para otros campos, comportamiento normal
          setDismissedNotifications((prev) => ({ ...prev, [field]: true }));
        }
      } finally {
        setLoadingDismiss((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de descripción de servicios individuales
    const handleFixServiceDescription = async (serviceIndex, serviceName) => {
      const loadingKey = `service_${serviceIndex}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.serviceItems.${serviceIndex}.description`,
          serviceName: serviceName
        });

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          setDismissedNotifications((prev) => ({
            ...prev,
            [loadingKey]: true
          }));
        }
      } catch (error) {
        console.error('Error calling fix service description endpoint:', error);
        console.error('Error response:', error.response?.data);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // Función para manejar el fix de servicios recomendados
    const handleFixRecommendedService = async (serviceId) => {
      // Verificar si hay alguna operación en progreso
      if (isAnyOperationInProgress()) {
        return;
      }

      const loadingKey = `recommended_service_${serviceId}`;

      setLoadingFix((prev) => ({ ...prev, [loadingKey]: true }));

      try {
        const response = await axios.post(API_ENDPOINT_FIX_FIELD, {
          clientId: selectedClient?.id,
          fieldPath: `recomendations.serviceItems.recommendedServices.${serviceId}`,
          serviceId: serviceId
        });

        // Solo cerrar la notificación si el status es 200
        if (response.status === 200) {
          setDismissedNotifications((prev) => ({
            ...prev,
            [loadingKey]: true,
            // También cerrar la notificación general de servicios recomendados
            recommended_services_general: true
          }));

          // Agregar el servicio recomendado a la lista de servicios existentes
          if (recommendedServices) {
            const serviceToAdd = recommendedServices.find(
              (s) => s.id === serviceId
            );
            if (serviceToAdd) {
              // Aquí podrías agregar el servicio a la lista existente
              // Por ahora solo cerramos la notificación
            }
          }
        }
      } catch (error) {
        console.error('Error calling fix recommended service endpoint:', error);
        console.error('Error response:', error.response?.data);
      } finally {
        setLoadingFix((prev) => ({ ...prev, [loadingKey]: false }));
      }
    };

    // FUNCIÓN HELPER PARA PARSEAR RECOMENDACIONES DE SERVICIOS
    const parseServiceRecommendations = (rawMessage) => {
      try {
        return JSON.parse(rawMessage);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        return [];
      }
    };

    // FUNCIÓN HELPER PARA PARSEAR SERVICIOS RECOMENDADOS
    const parseRecommendedServices = (rawMessage) => {
      try {
        const parsed = JSON.parse(rawMessage);
        if (Array.isArray(parsed)) {
          return parsed
            .map((item, index) => {
              if (item.freeFormServiceItem && item.freeFormServiceItem.label) {
                return {
                  id: `recommended_${index}`,
                  name: item.freeFormServiceItem.label.displayName,
                  description: item.freeFormServiceItem.label.description,
                  category: item.freeFormServiceItem.category,
                  isRecommended: true
                };
              }
              return null;
            })
            .filter(Boolean);
        }
        return [];
      } catch (parseError) {
        console.error('JSON parse error for recommended services:', parseError);
        return [];
      }
    };

    // FUNCIÓN PARA BUSCAR EL CAMPO DE SERVICIOS
    const findServicesField = (data) => {
      if (!data) return null;

      // Primero, verificar el campo estándar de Google Business Profile: serviceItems
      if (data.serviceItems && Array.isArray(data.serviceItems)) {
        return data.serviceItems;
      }

      // Buscar campos que puedan contener servicios
      const possibleFields = Object.keys(data).filter(
        (key) =>
          key.toLowerCase().includes('service') ||
          key.toLowerCase().includes('servicio') ||
          key.toLowerCase().includes('services')
      );

      // Verificar cada campo para ver si contiene datos de servicios
      for (const field of possibleFields) {
        const fieldData = data[field];

        if (Array.isArray(fieldData) && fieldData.length > 0) {
          const firstItem = fieldData[0];
          if (
            firstItem.structuredServiceItem ||
            firstItem.freeFormServiceItem
          ) {
            return fieldData;
          }
        }
      }

      // Si no encontramos nada, buscar en el campo servicesList directamente
      if (data.servicesList && Array.isArray(data.servicesList)) {
        return data.servicesList;
      }

      return null;
    };

    // FUNCIÓN PARA PROCESAR LA LISTA DE SERVICIOS
    const processServicesList = (servicesData) => {
      if (!servicesData || !Array.isArray(servicesData)) {
        return null;
      }

      const processedServices = [];

      servicesData.forEach((service) => {
        if (service.structuredServiceItem) {
          const { label, serviceTypeId, description } =
            service.structuredServiceItem;

          // Caso 1: structuredServiceItem con label (estructura de recomendaciones)
          if (label && label.displayName) {
            processedServices.push({
              name: label.displayName,
              description: label.description || ''
            });
          }
          // Caso 2: structuredServiceItem con serviceTypeId (estructura real de Google Business Profile)
          else if (serviceTypeId) {
            // Extraer el nombre del servicio del serviceTypeId y hacerlo más legible
            const serviceName = serviceTypeId
              .replace('job_type_id:', '')
              .replace(/_/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            processedServices.push({
              name: serviceName,
              description: description || '' // Usar la descripción directa del structuredServiceItem si existe
            });
          }
        } else if (service.freeFormServiceItem) {
          const { label } = service.freeFormServiceItem;
          if (label && label.displayName) {
            processedServices.push({
              name: label.displayName,
              description: label.description || ''
            });
          }
        }
      });

      return processedServices;
    };

    // FUNCIÓN PARA CLASIFICAR SERVICIOS POR CATEGORÍAS
    const classifyServicesByCategory = (
      servicesData,
      recommendedServices = []
    ) => {
      const servicesByCategory = {};

      // Procesar servicios existentes solo si existen
      if (servicesData && Array.isArray(servicesData)) {
        servicesData.forEach((service) => {
          let categoryName = 'Other Services';
          let serviceInfo = null;

          if (service.freeFormServiceItem) {
            const { category, label } = service.freeFormServiceItem;
            if (category) {
              // Buscar la categoría existente que coincida con este ID
              let foundCategory = null;

              // Verificar categoría principal
              if (
                googleProfileInfo.data?.categories?.primaryCategory?.name ===
                category
              ) {
                foundCategory =
                  googleProfileInfo.data.categories.primaryCategory.displayName;
              }

              // Verificar categorías adicionales
              if (
                !foundCategory &&
                googleProfileInfo.data?.categories?.additionalCategories
              ) {
                const additionalCategory =
                  googleProfileInfo.data.categories.additionalCategories.find(
                    (cat) => cat.name === category
                  );
                if (additionalCategory) {
                  foundCategory = additionalCategory.displayName;
                }
              }

              // Si se encuentra una categoría existente, usar su displayName
              if (foundCategory) {
                categoryName = foundCategory;
              } else {
                // Si no se encuentra, generar nombre desde el ID como fallback
                categoryName = category
                  .replace('categories/gcid:', '')
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
              }
            } else {
              // Si no tiene categoría específica, usar la categoría principal del negocio
              categoryName =
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName || 'Other Services';
            }

            if (label && label.displayName) {
              serviceInfo = {
                name: label.displayName,
                description: label.description || '',
                type: 'freeForm'
              };
            }
          } else if (service.structuredServiceItem) {
            const { serviceTypeId, description, category } =
              service.structuredServiceItem;

            // Verificar si el servicio estructurado tiene categoría específica
            if (category) {
              // Extraer el nombre de la categoría del ID
              categoryName = category
                .replace('categories/gcid:', '')
                .replace(/_/g, ' ')
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
            } else {
              // Buscar en additionalCategories para hacer match con serviceTypeId
              const additionalCategories =
                googleProfileInfo.data?.categories?.additionalCategories || [];
              let foundCategory = null;

              // Buscar en cada categoría adicional
              for (const additionalCategory of additionalCategories) {
                if (additionalCategory.serviceTypes) {
                  const matchingServiceType =
                    additionalCategory.serviceTypes.find(
                      (serviceType) =>
                        serviceType.serviceTypeId === serviceTypeId
                    );
                  if (matchingServiceType) {
                    foundCategory = additionalCategory.displayName;
                    break;
                  }
                }
              }

              // Usar la categoría encontrada o la categoría principal del negocio
              categoryName =
                foundCategory ||
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName ||
                'Other Services';
            }

            const serviceName = serviceTypeId
              .replace('job_type_id:', '')
              .replace(/_/g, ' ')
              .split(' ')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');

            serviceInfo = {
              name: serviceName,
              description: description || '',
              type: 'structured'
            };
          }

          if (serviceInfo) {
            if (!servicesByCategory[categoryName]) {
              servicesByCategory[categoryName] = [];
            }
            servicesByCategory[categoryName].push(serviceInfo);
          }
        });
      }

      // Procesar servicios recomendados (SIEMPRE, incluso si no hay servicios existentes)
      if (recommendedServices && recommendedServices.length > 0) {
        recommendedServices.forEach((recommendedService) => {
          let categoryName = 'Other Services';

          if (recommendedService.category) {
            // Normalizar el ID de categoría para hacer match con las categorías existentes
            const normalizedCategoryId = recommendedService.category.startsWith(
              'gcid:'
            )
              ? `categories/${recommendedService.category}`
              : recommendedService.category;

            // Buscar la categoría existente que coincida
            let foundCategory = null;

            // Verificar categoría principal
            if (
              googleProfileInfo.data?.categories?.primaryCategory?.name ===
              normalizedCategoryId
            ) {
              foundCategory =
                googleProfileInfo.data.categories.primaryCategory.displayName;
            }

            // Verificar categorías adicionales
            if (
              !foundCategory &&
              googleProfileInfo.data?.categories?.additionalCategories
            ) {
              const additionalCategory =
                googleProfileInfo.data.categories.additionalCategories.find(
                  (cat) => cat.name === normalizedCategoryId
                );
              if (additionalCategory) {
                foundCategory = additionalCategory.displayName;
              }
            }

            // Si se encuentra una categoría existente, usar su displayName
            if (foundCategory) {
              categoryName = foundCategory;
            } else {
              // Si no se encuentra, usar la categoría principal como fallback
              categoryName =
                googleProfileInfo.data?.categories?.primaryCategory
                  ?.displayName || 'Other Services';
            }
          } else {
            // Si no tiene categoría específica, usar la categoría principal del negocio
            categoryName =
              googleProfileInfo.data?.categories?.primaryCategory
                ?.displayName || 'Other Services';
          }

          const serviceInfo = {
            id: recommendedService.id, // Incluir el ID del servicio recomendado
            name: recommendedService.name,
            description: recommendedService.description || '',
            type: 'recommended',
            isRecommended: true
          };

          if (!servicesByCategory[categoryName]) {
            servicesByCategory[categoryName] = [];
          }
          servicesByCategory[categoryName].push(serviceInfo);
        });
      } else {
      }

      return servicesByCategory;
    };

    // FUNCIÓN PARA RENDERIZAR LA LISTA DE SERVICIOS AGRUPADOS POR CATEGORÍA
    const renderServicesList = (originalServicesData, allServices) => {
      if (!allServices || allServices.length === 0) {
        return '—';
      }

      // Usar directamente los allServices que se pasan como parámetro
      // Estos ya incluyen servicios recomendados y descripciones actualizadas

      // Obtener servicios recomendados de allServices
      const recommendedServicesList = allServices.filter(
        (service) => service.isRecommended
      );

      // Clasificar servicios por categoría usando los datos originales y servicios recomendados

      const servicesByCategory = classifyServicesByCategory(
        originalServicesData,
        recommendedServicesList
      );

      // Agregar servicios de fixedServicesList que no están en originalServicesData ni en recommendedServicesList
      if (fixedServicesList) {
        fixedServicesList.forEach((service) => {
          // Verificar si el servicio ya está clasificado
          const isAlreadyClassified = Object.values(servicesByCategory).some(
            (categoryServices) =>
              categoryServices.some(
                (classifiedService) => classifiedService.name === service.name
              )
          );

          if (!isAlreadyClassified && !service.isRecommended) {
            // Determinar la categoría del servicio
            let categoryName = service.category || 'Other Services';

            // Si la categoría es un ID, convertirla a display name
            if (
              categoryName.includes('gcid:') ||
              categoryName.includes('categories/')
            ) {
              const normalizedCategoryId = categoryName.startsWith('gcid:')
                ? `categories/${categoryName}`
                : categoryName;

              // Buscar en categorías existentes
              if (
                googleProfileInfo.data?.categories?.primaryCategory?.name ===
                normalizedCategoryId
              ) {
                categoryName =
                  googleProfileInfo.data.categories.primaryCategory.displayName;
              } else if (
                googleProfileInfo.data?.categories?.additionalCategories
              ) {
                const foundCategory =
                  googleProfileInfo.data.categories.additionalCategories.find(
                    (cat) => cat.name === normalizedCategoryId
                  );
                if (foundCategory) {
                  categoryName = foundCategory.displayName;
                }
              }
            }

            // Agregar el servicio a la categoría correspondiente
            if (!servicesByCategory[categoryName]) {
              servicesByCategory[categoryName] = [];
            }

            servicesByCategory[categoryName].push({
              name: service.name,
              description: service.description || '',
              type: 'added',
              isAdded: service.isAdded || false
            });
          }
        });
      }

      const categoryNames = Object.keys(servicesByCategory);

      if (categoryNames.length === 0) {
        return '—';
      }

      return (
        <div>
          {categoryNames.map((categoryName) => {
            const categoryServices = servicesByCategory[categoryName];

            // Usar directamente los servicios de la categoría (ya incluyen servicios recomendados)
            const processedCategoryServices = categoryServices;

            // Filtrar servicios visibles para el contador
            const visibleServices = processedCategoryServices.filter(
              (service) => {
                // Si es un servicio recomendado y ha sido dismissado, no mostrarlo
                if (service.isRecommended) {
                  const isDismissed =
                    dismissedNotifications[
                      `service_${service.name}_recommended`
                    ];
                  return !isDismissed;
                }
                // Mostrar todos los servicios existentes
                return true;
              }
            );

            const serviceCount = visibleServices.length;

            return (
              <div key={categoryName} className="services-category-section">
                <div className="category-header">
                  <span
                    style={{
                      backgroundColor: '#d1ecf1',
                      color: '#0c5460',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      marginBottom: '12px',
                      display: 'inline-block',
                      border: '1px solid #bee5eb'
                    }}>
                    {categoryName} ({serviceCount})
                  </span>
                </div>
                <div className="services-list">
                  {visibleServices
                    .map((service, index) => {
                      // Aplicar descripciones actualizadas si están disponibles
                      let serviceToRender = service;
                      if (allServices && !service.isRecommended) {
                        const updatedService = allServices.find(
                          (updated) => updated.name === service.name
                        );
                        if (updatedService) {
                          serviceToRender = {
                            ...service,
                            description: updatedService.description
                          };
                        }
                      }
                      return serviceToRender;
                    })
                    .map((service, index) => {
                      // Para servicios recomendados, renderizar completamente dentro del box azul
                      if (service.isRecommended) {
                        // Si ya fue añadido, no mostrar nada
                        if (addedRecommendedServices.has(service.name)) {
                          return null;
                        }

                        // Si fue dismissado, no mostrar nada
                        if (
                          dismissedNotifications[
                            `service_${service.name}_recommended`
                          ]
                        ) {
                          return null;
                        }

                        // Renderizar el servicio completo dentro del box de recomendación
                        return (
                          <div
                            key={`${service.name}-${index}`}
                            className="mt-3 p-4 rounded-xl border bg-blue-50 border-blue-200 text-blue-800">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                              <div className="flex-1">
                                <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
                                  Suggestion:
                                </span>
                                <div className="mt-2 flex items-center gap-2">
                                  <Briefcase className="h-5 w-5" />
                                  <span className="text-base font-semibold">
                                    {service.name}
                                  </span>
                                </div>
                                {service.description && (
                                  <p className="text-sm opacity-80 mt-2 pl-7">
                                    {service.description}
                                  </p>
                                )}
                                <div className="text-sm opacity-80 mt-2">
                                  <strong>Suggestion:</strong> Add this
                                  recommended service to improve your business
                                  offerings.
                                </div>
                              </div>
                              <div className="flex gap-2 flex-shrink-0">
                                <Button
                                  size="sm"
                                  onClick={async () => {
                                    const serviceId = service.id;
                                    const fieldPath = `serviceItems.recommendedServices.${serviceId}`;

                                    await handleFixField(fieldPath, {
                                      serviceId
                                    });

                                    setAddedRecommendedServices(
                                      (prev) => new Set([...prev, service.name])
                                    );
                                  }}
                                  disabled={
                                    loadingFix[
                                      `recommended_service_${service.id}`
                                    ] ||
                                    (isAnyOperationInProgress &&
                                      isAnyOperationInProgress())
                                  }
                                  className="h-9 px-4 rounded-lg gap-2 font-semibold bg-green-600 hover:bg-green-700 text-white">
                                  {loadingFix[
                                    `recommended_service_${service.id}`
                                  ] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4" />
                                  )}
                                  Fix
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    handleDismissNotification(
                                      `service_${service.name}_recommended`
                                    )
                                  }
                                  disabled={
                                    loadingDismiss[
                                      `service_${service.name}_recommended`
                                    ] ||
                                    (isAnyOperationInProgress &&
                                      isAnyOperationInProgress())
                                  }
                                  className="h-9 px-4 rounded-lg gap-2 border-gray-300 hover:bg-gray-100">
                                  {loadingDismiss[
                                    `service_${service.name}_recommended`
                                  ] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <X className="h-4 w-4" />
                                  )}
                                  Dismiss
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      }

                      // Para servicios existentes, mantener el formato actual
                      return (
                        <div
                          key={`${service.name}-${index}`}
                          className="p-4 bg-muted/30 rounded-xl space-y-2">
                          <div className="flex items-center justify-between">
                            <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                              <Briefcase className="h-5 w-5 text-muted-foreground" />
                              {service.name}
                            </p>
                            <div className="flex gap-2">
                              {service.isAdded && (
                                <Badge
                                  variant="default"
                                  className="text-xs bg-green-600">
                                  Added
                                </Badge>
                              )}
                              {!service.description && (
                                <Badge variant="outline" className="text-xs">
                                  Needs attention
                                </Badge>
                              )}
                            </div>
                          </div>
                          {service.description ? (
                            <p className="text-sm text-muted-foreground pl-7">
                              {service.description}
                            </p>
                          ) : (
                            <p className="text-sm text-muted-foreground pl-7">
                              {service.name} needs a description
                            </p>
                          )}

                          {/* Indicador de loading individual para cada servicio - DENTRO del box */}
                          {loadingAgent && !service.isAdded && (
                            <div style={{ marginTop: 12 }}>
                              <div
                                className="flex items-center gap-2"
                                style={{ minHeight: 40 }}>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span className="text-sm">
                                  Analyzing {service.name}...
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Notificaciones individuales para servicios existentes */}
                          {showQualityAgent &&
                            !loadingAgent &&
                            qualityAgentData &&
                            !service.isAdded && (
                              <div style={{ marginTop: 12 }}>
                                {(() => {
                                  // SEGUNDO: Para servicios existentes (no recomendados)
                                  const hasDescription =
                                    service.description &&
                                    service.description.trim() !== '';

                                  if (hasDescription) {
                                    // Servicio existente con descripción - notificación verde (solo si no está dismissada)
                                    return !dismissedNotifications[
                                      `service_${service.name}_good`
                                    ] ? (
                                      <QualityAgentNotification
                                        status="good"
                                        message={`${service.name} description is OK.`}
                                        onDismiss={() =>
                                          handleDismissNotification(
                                            `service_${service.name}_good`
                                          )
                                        }
                                        loadingDismiss={
                                          loadingDismiss[
                                            `service_${service.name}_good`
                                          ]
                                        }
                                      />
                                    ) : null;
                                  } else {
                                    // Servicio existente sin descripción - notificación roja con sugerencia (solo si no está dismissada)
                                    if (
                                      dismissedNotifications[
                                        `service_${service.name}_bad`
                                      ]
                                    ) {
                                      return null;
                                    }

                                    const getServiceSpecificSuggestion = (
                                      serviceName
                                    ) => {
                                      try {
                                        const serviceSuggestionData =
                                          qualityAgentData?.recomendations
                                            ?.serviceItems
                                            ?.descriptionKeywordAnalysis
                                            ?.message;
                                        if (serviceSuggestionData) {
                                          const parsedSuggestions = JSON.parse(
                                            serviceSuggestionData
                                          );
                                          if (
                                            Array.isArray(parsedSuggestions)
                                          ) {
                                            // Buscar la sugerencia específica para este servicio
                                            const specificSuggestion =
                                              parsedSuggestions.find(
                                                (suggestion) => {
                                                  // Verificar freeFormServiceItem
                                                  if (
                                                    suggestion
                                                      .freeFormServiceItem
                                                      ?.label?.displayName
                                                  ) {
                                                    return (
                                                      suggestion.freeFormServiceItem.label.displayName.toLowerCase() ===
                                                      serviceName.toLowerCase()
                                                    );
                                                  }
                                                  // Verificar structuredServiceItem por displayName (nueva estructura)
                                                  if (
                                                    suggestion
                                                      .structuredServiceItem
                                                      ?.label?.displayName
                                                  ) {
                                                    return (
                                                      suggestion.structuredServiceItem.label.displayName.toLowerCase() ===
                                                      serviceName.toLowerCase()
                                                    );
                                                  }
                                                  // Verificar structuredServiceItem por serviceTypeId (estructura legacy)
                                                  if (
                                                    suggestion
                                                      .structuredServiceItem
                                                      ?.serviceTypeId
                                                  ) {
                                                    const serviceTypeName =
                                                      suggestion.structuredServiceItem.serviceTypeId
                                                        .replace(
                                                          'job_type_id:',
                                                          ''
                                                        )
                                                        .replace(/_/g, ' ')
                                                        .split(' ')
                                                        .map(
                                                          (word) =>
                                                            word
                                                              .charAt(0)
                                                              .toUpperCase() +
                                                            word.slice(1)
                                                        )
                                                        .join(' ');
                                                    return (
                                                      serviceTypeName.toLowerCase() ===
                                                      serviceName.toLowerCase()
                                                    );
                                                  }
                                                  return false;
                                                }
                                              );

                                            if (specificSuggestion) {
                                              // Priorizar freeFormServiceItem description
                                              if (
                                                specificSuggestion
                                                  .freeFormServiceItem?.label
                                                  ?.description
                                              ) {
                                                return specificSuggestion
                                                  .freeFormServiceItem.label
                                                  .description;
                                              }
                                              // Usar structuredServiceItem description - nueva estructura
                                              if (
                                                specificSuggestion
                                                  .structuredServiceItem?.label
                                                  ?.description
                                              ) {
                                                return specificSuggestion
                                                  .structuredServiceItem.label
                                                  .description;
                                              }
                                              // Usar structuredServiceItem description - estructura legacy
                                              if (
                                                specificSuggestion
                                                  .structuredServiceItem
                                                  ?.description
                                              ) {
                                                return specificSuggestion
                                                  .structuredServiceItem
                                                  .description;
                                              }
                                            }
                                          }
                                        }
                                      } catch (error) {
                                        console.error(
                                          'Error parsing service suggestions:',
                                          error
                                        );
                                      }

                                      // Fallback: sugerencia genérica
                                      return `Add a detailed description for ${serviceName} to help customers understand what this service includes.`;
                                    };

                                    const serviceSpecificSuggestion =
                                      getServiceSpecificSuggestion(
                                        service.name
                                      );

                                    return (
                                      <QualityAgentNotification
                                        status="bad"
                                        message={`${service.name} needs a description.`}
                                        onFix={async () => {
                                          // Obtener servicios actuales (sin servicios recomendados) para el índice correcto
                                          const servicesData =
                                            findServicesField(
                                              googleProfileInfo.data
                                            );
                                          const currentServices =
                                            processServicesList(servicesData) ||
                                            [];

                                          // Generar un fieldPath específico para este servicio individual
                                          const serviceIndex =
                                            currentServices.findIndex(
                                              (s) => s.name === service.name
                                            );

                                          if (serviceIndex === -1) {
                                            console.error(
                                              `Service ${service.name} not found in current services`
                                            );
                                            return;
                                          }

                                          const fieldPath = `serviceItems.${serviceIndex}.description`;

                                          await handleFixField(fieldPath, {
                                            serviceName: service.name,
                                            serviceIndex: serviceIndex
                                          });
                                        }}
                                        onDismiss={() =>
                                          handleDismissNotification(
                                            `service_${service.name}_bad`
                                          )
                                        }
                                        suggestion={serviceSpecificSuggestion}
                                        loadingFix={
                                          loadingFix[
                                            `service_${service.name}_individual`
                                          ]
                                        }
                                        loadingDismiss={
                                          loadingDismiss[
                                            `service_${service.name}_bad`
                                          ]
                                        }
                                        isAnyOperationInProgress={
                                          isAnyOperationInProgress
                                        }
                                      />
                                    );
                                  }
                                })()}
                              </div>
                            )}
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}

          {/* Notificaciones del Quality Agent para servicios */}
          {showQualityAgent && !loadingAgent && qualityAgentData && (
            <>
              {/* Notificación general para servicios recomendados */}
              {qualityAgentData?.recomendations?.serviceItems
                ?.recommendedServices?.message &&
                !dismissedNotifications['recommended_services_general'] &&
                // Verificar si algún servicio recomendado individual ya fue añadido
                addedRecommendedServices.size === 0 &&
                // Verificar si algún servicio recomendado individual ha sido dismissado
                !(() => {
                  if (
                    !recommendedServicesList ||
                    recommendedServicesList.length === 0
                  )
                    return false;
                  return recommendedServicesList.some(
                    (service) =>
                      dismissedNotifications[
                        `service_${service.name}_recommended`
                      ]
                  );
                })() && (
                  <div style={{ marginTop: 16 }}>
                    <QualityAgentNotification
                      status="recommended"
                      message={`We have ${
                        recommendedServicesList
                          ? recommendedServicesList.length
                          : 0
                      } additional service recommendations for your business.`}
                      onFix={
                        recommendedServicesList &&
                        recommendedServicesList.length > 0
                          ? () =>
                              handleFixField('serviceItems.recommendedServices')
                          : null
                      }
                      onDismiss={() =>
                        handleDismissNotification(
                          'recommended_services_general'
                        )
                      }
                      suggestion={
                        recommendedServicesList &&
                        recommendedServicesList.length > 0
                          ? 'Click Fix All to add all recommended services to your business profile and expand your service offerings.'
                          : 'No additional service recommendations available at this time.'
                      }
                      loadingFix={loadingFix['recommended_services_general']}
                      loadingDismiss={
                        loadingDismiss['recommended_services_general']
                      }
                      fixButtonText="Fix All"
                      isAnyOperationInProgress={isAnyOperationInProgress}
                    />
                  </div>
                )}
            </>
          )}
        </div>
      );
    };

    // Función para calcular el porcentaje de optimización del perfil
    const calculateOptimizationPercentage = () => {
      if (!googleProfileInfo.data) return null;

      const fields = [];
      const totalFields = 8; // Total de condiciones a evaluar
      const conditionsStatus = {}; // Para trackear el estado de cada condición

      // 1. Description (considerar fixedDescription si está disponible)
      const description =
        fixedDescription !== null
          ? fixedDescription
          : googleProfileInfo.data?.profile?.description;
      const hasDescription = description && description.trim() !== '';
      conditionsStatus['1. Description'] = {
        fulfilled: hasDescription,
        value: description ? description.substring(0, 50) + '...' : 'N/A'
      };
      if (hasDescription) {
        fields.push('description');
      }

      // 2. Service Area (considerar fixedServiceArea si está disponible)
      const serviceArea =
        fixedServiceArea !== null
          ? fixedServiceArea
          : googleProfileInfo.data?.serviceArea?.places?.placeInfos;
      const hasServiceArea = serviceArea && serviceArea.length > 0;
      conditionsStatus['2. Service Area'] = {
        fulfilled: hasServiceArea,
        value: hasServiceArea ? `${serviceArea.length} areas` : 'No areas'
      };
      if (hasServiceArea) {
        fields.push('serviceArea');
      }

      // 3. Review Frequency
      const reviewFrequency =
        googleProfileInfo.data?.reviews_info?.reviewFrecuency;
      const hasReviewFrequency =
        reviewFrequency && parseFloat(reviewFrequency) >= 1;
      conditionsStatus['3. Review Frequency'] = {
        fulfilled: hasReviewFrequency,
        value: reviewFrequency ? `${reviewFrequency} (need >= 1)` : 'N/A'
      };
      if (hasReviewFrequency) {
        fields.push('reviewFrequency');
      }

      // 4. Google Rating
      const averageRating = googleProfileInfo.data?.reviews_info?.averageRating;
      const hasGoodRating = averageRating && parseFloat(averageRating) >= 4;
      conditionsStatus['4. Google Rating'] = {
        fulfilled: hasGoodRating,
        value: averageRating ? `${averageRating} (need >= 4)` : 'N/A'
      };
      if (hasGoodRating) {
        fields.push('googleRating');
      }

      // 5. Service List (servicios > 0 y cada servicio debe tener descripción)
      const servicesData = findServicesField(googleProfileInfo.data);
      const currentServices = processServicesList(servicesData);
      // Usar fixedServicesList si está disponible
      const servicesToCheck =
        fixedServicesList !== null ? fixedServicesList : currentServices;
      let hasValidServices = false;
      if (servicesToCheck && servicesToCheck.length > 0) {
        const allServicesHaveDescription = servicesToCheck.every(
          (service) => service.description && service.description.trim() !== ''
        );
        hasValidServices = allServicesHaveDescription;
        conditionsStatus['5. Service List'] = {
          fulfilled: hasValidServices,
          value: `${servicesToCheck.length} services, all with description: ${allServicesHaveDescription}`
        };
        if (allServicesHaveDescription) {
          fields.push('serviceList');
        }
      } else {
        conditionsStatus['5. Service List'] = {
          fulfilled: false,
          value: 'No services'
        };
      }

      // 6. Review Replies
      const reviewsReplied =
        googleProfileInfo.data?.reviews_info?.reviewsReplied;
      const hasReviewReplies =
        reviewsReplied && parseFloat(reviewsReplied) >= 1;
      conditionsStatus['6. Review Replies'] = {
        fulfilled: hasReviewReplies,
        value: reviewsReplied ? `${reviewsReplied} (need >= 1)` : 'N/A'
      };
      if (hasReviewReplies) {
        fields.push('reviewReplies');
      }

      // 7. Social Networks (al menos 1 conectada)
      const socialNetworks = selectedClient?.socialnetworks || {};
      const connectedSocialNetworks = [
        Boolean(socialNetworks.facebook),
        Boolean(socialNetworks.instagram),
        Boolean(socialNetworks.googleMyBusiness),
        Boolean(socialNetworks.tiktok),
        Boolean(socialNetworks.twitter)
      ].filter(Boolean).length;
      const hasSocialNetworks = connectedSocialNetworks > 0;
      conditionsStatus['7. Social Networks'] = {
        fulfilled: hasSocialNetworks,
        value: `${connectedSocialNetworks} connected`
      };
      if (hasSocialNetworks) {
        fields.push('socialNetworks');
      }

      // 8. Post Keyword Optimization (considerar fixedPostsKeywords si está disponible)
      const postsKeywords =
        fixedPostsKeywords !== null
          ? fixedPostsKeywords
          : googleProfileInfo.data?.posts_info?.posts_keywords || '';
      const hasPostsKeywords = postsKeywords && postsKeywords.trim() !== '';

      conditionsStatus['8. Post Keywords'] = {
        fulfilled: hasPostsKeywords,
        value: hasPostsKeywords
          ? `"${postsKeywords.substring(0, 50)}..."`
          : 'No keywords set',
        debug: {
          fixedPostsKeywords,
          actualValue: googleProfileInfo.data?.posts_info?.posts_keywords,
          usedValue: postsKeywords
        }
      };
      if (hasPostsKeywords) {
        fields.push('postKeywordOptimization');
      }

      // Log detallado del estado de cada condición (comentado para evitar spam)
      Object.entries(conditionsStatus).forEach(([condition, status]) => {
        if (status.debug) {
        }
      });

      const percentage = Math.round((fields.length / totalFields) * 100) || 0;
      const criteria = {
        description: conditionsStatus['1. Description']?.fulfilled || false,
        serviceArea: conditionsStatus['2. Service Area']?.fulfilled || false,
        reviewFrequency:
          conditionsStatus['3. Review Frequency']?.fulfilled || false,
        googleRating: conditionsStatus['4. Google Rating']?.fulfilled || false,
        serviceList: conditionsStatus['5. Service List']?.fulfilled || false,
        reviewReplies:
          conditionsStatus['6. Review Replies']?.fulfilled || false,
        socialNetworks:
          conditionsStatus['7. Social Networks']?.fulfilled || false,
        postKeywords: conditionsStatus['8. Post Keywords']?.fulfilled || false
      };
      return { percentage, criteria };
    };

    // Calcular el porcentaje de optimización usando los datos internos actualizados
    const {
      percentage: calculatedOptimizationPercentage,
      criteria: calculatedCriteria
    } = calculateOptimizationPercentage() || { percentage: 0, criteria: null };

    // Ref para evitar llamadas redundantes a onCriteriaUpdate
    const prevCriteriaStringRef = useRef(null);

    // Notificar al padre cuando cambie el porcentaje de optimización
    useEffect(() => {
      if (onOptimizationChange && typeof onOptimizationChange === 'function') {
        onOptimizationChange(calculatedOptimizationPercentage);
      }
      if (
        onCriteriaUpdate &&
        typeof onCriteriaUpdate === 'function' &&
        calculatedCriteria
      ) {
        const criteriaStr = JSON.stringify(calculatedCriteria);
        if (prevCriteriaStringRef.current !== criteriaStr) {
          prevCriteriaStringRef.current = criteriaStr;
          onCriteriaUpdate(calculatedCriteria);
        }
      }
    }, [
      calculatedOptimizationPercentage,
      onOptimizationChange,
      onCriteriaUpdate
    ]);

    // Notificar al padre cuando haya cambios sin guardar
    useEffect(() => {
      if (onChangesUpdate && typeof onChangesUpdate === 'function') {
        const hasChanges = Object.keys(editedFields).length > 0;
        onChangesUpdate(hasChanges);
      }
    }, [editedFields, onChangesUpdate]);

    return (
      <div style={{ position: 'inherit' }}>
        {googleProfileInfo.loading ? (
          <div className="space-y-6">
            {/* Skeleton para Profile Optimization Rating */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-16 w-16 rounded-full" />
                </div>
              </CardContent>
            </Card>

            {/* Skeleton para About your business */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-7 w-64" />
                    <Skeleton className="h-4 w-96" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-5 w-32" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Skeleton para Categories */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-7 w-40" />
                    <Skeleton className="h-4 w-80" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                <div className="space-y-4">
                  <Skeleton className="h-10 w-full" />
                  <div className="flex gap-2">
                    <Skeleton className="h-8 w-24 rounded-full" />
                    <Skeleton className="h-8 w-32 rounded-full" />
                    <Skeleton className="h-8 w-28 rounded-full" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Skeleton para Description */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-7 w-48" />
                    <Skeleton className="h-4 w-96" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </CardContent>
            </Card>

            {/* Skeleton para Services */}
            <Card className="shadow-md rounded-2xl border-0">
              <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-7 w-56" />
                    <Skeleton className="h-4 w-80" />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="p-4 bg-muted/30 rounded-xl space-y-2">
                      <Skeleton className="h-5 w-40" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Mensajes de error */}
            {saveMessage.show && saveMessage.type === 'error' && (
              <div className="bg-destructive/10 text-destructive border border-destructive/20 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <i className="fas fa-exclamation-circle"></i>
                  <span className="text-sm">{saveMessage.message}</span>
                </div>
              </div>
            )}
            {/* Content */}
            <div className="space-y-6">
              {/* About your business */}
              <Card
                id="about"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        About your business
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Basic information that appears on your Google listing
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <InfoField
                    title={
                      <span className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 text-muted-foreground" />
                        Business name
                      </span>
                    }
                    description="The official name of your business as it appears on Google"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields?.businessName}
                    hasChanges={!!editedFields.businessName}
                    onFieldChange={(value) =>
                      handleFieldChange('businessName', value)
                    }>
                    {googleProfileInfo.data?.title
                      ? googleProfileInfo.data?.title
                      : '—'}
                  </InfoField>

                  <Categories
                    title={
                      <span className="flex items-center gap-2">
                        <Tag className="h-5 w-5 text-muted-foreground" />
                        Business categories
                      </span>
                    }
                    description="Categories help customers find your business in Google searches"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields.businessCategory}
                    categories={categories}
                    googleProfileInfo={
                      googleProfileInfo.data?.categories?.primaryCategory
                        ?.displayName
                    }
                    additionalCategories={
                      googleProfileInfo.data?.categories?.additionalCategories
                    }
                    onFieldChange={(value) =>
                      handleFieldChange('businessCategory', value)
                    }>
                    {googleProfileInfo.data?.categories?.primaryCategory
                      ?.displayName
                      ? googleProfileInfo.data?.categories.primaryCategory
                          .displayName
                      : '—'}
                  </Categories>

                  {/* Notificaciones individuales para categorías recomendadas */}
                  {showQualityAgent && (
                    <div style={{ marginTop: 8 }}>
                      {loadingAgent ? (
                        <div
                          className="flex items-center gap-2"
                          style={{ minHeight: 60 }}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Analyzing categories...</span>
                        </div>
                      ) : (
                        recommendedCategories &&
                        recommendedCategories.length > 0 &&
                        recommendedCategories.map((category, index) => (
                          <div
                            key={category.id || index}
                            style={{ marginBottom: 8 }}>
                            <QualityAgentNotification
                              status="recommended"
                              message={category.display_name}
                              onFix={() =>
                                handleFixIndividualCategory(category)
                              }
                              onDismiss={() =>
                                handleDismissIndividualCategory(category.id)
                              }
                              suggestion={`This category would help customers find your business when searching for "${category.display_name.toLowerCase()}" services.`}
                              loadingFix={loadingFix[`category_${category.id}`]}
                              loadingDismiss={
                                loadingDismiss[`category_${category.id}`]
                              }
                              fixButtonText="Add"
                              isAnyOperationInProgress={
                                isAnyOperationInProgress
                              }
                            />
                          </div>
                        ))
                      )}
                    </div>
                  )}

                  <InfoField
                    title={
                      <span className="flex items-center gap-2">
                        <FileText className="h-5 w-5 text-muted-foreground" />
                        Description
                      </span>
                    }
                    description="A brief description of what your business offers to customers"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields.description}
                    hasChanges={!!editedFields.description}
                    multiline={true}
                    onFieldChange={(value) =>
                      handleFieldChange('description', value)
                    }>
                    {visualDescription ? visualDescription : '—'}
                  </InfoField>
                  {showQualityAgent && !dismissedNotifications.description && (
                    <div style={{ marginTop: 8 }}>
                      {loadingAgent ? (
                        <div
                          className="flex items-center gap-2"
                          style={{ minHeight: 60 }}>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Analyzing description...</span>
                        </div>
                      ) : qualityAgentData ? (
                        <QualityAgentNotification
                          status={isDescriptionGood ? 'good' : 'recommended'}
                          message={message}
                          onFix={
                            !isDescriptionGood
                              ? () => handleFixField('profile.description')
                              : undefined
                          }
                          onDismiss={() =>
                            handleDismissNotification('description')
                          }
                          suggestion={
                            !isDescriptionGood ? suggestion : undefined
                          }
                          loadingFix={loadingFix.description}
                          loadingDismiss={loadingDismiss.description}
                        />
                      ) : description ? (
                        <QualityAgentNotification
                          status="good"
                          message="Your description is high quality!"
                          onDismiss={() =>
                            handleDismissNotification('description')
                          }
                          loadingDismiss={loadingDismiss.description}
                        />
                      ) : null}
                    </div>
                  )}
                  {/* --- INICIO CAMPOS DE CALIDAD --- */}
                </CardContent>
              </Card>

              {/* Location and areas */}
              <Card
                id="location"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <MapPin className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Location and areas
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Where your business operates and serves customers
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {fixedServiceArea !== null ? (
                    // Renderizado directo cuando hay fixedServiceArea
                    <div className="info-field">
                      <div>
                        <div>
                          <strong>Service area:</strong>
                        </div>
                      </div>
                      <div style={{ marginTop: '14px' }}>
                        <div>
                          <span>
                            {(() => {
                              // Obtener las áreas de servicio originales
                              const originalServiceAreas =
                                googleProfileInfo.data?.serviceArea?.places
                                  ?.placeInfos || [];
                              const originalPlaceNames =
                                originalServiceAreas.map(
                                  (place) => place.placeName
                                );

                              // Obtener las recomendaciones
                              const recommendedPlaceNames =
                                fixedServiceArea.map((place) => place.name);

                              // Combinar originales + recomendaciones, evitando duplicados
                              const combinedPlaceNames = [
                                ...originalPlaceNames
                              ];

                              recommendedPlaceNames.forEach((placeName) => {
                                if (!combinedPlaceNames.includes(placeName)) {
                                  combinedPlaceNames.push(placeName);
                                }
                              });

                              return combinedPlaceNames.map(
                                (placeName, index) => (
                                  <span key={`${index}-${forceRender}`}>
                                    {placeName}
                                    <br />
                                  </span>
                                )
                              );
                            })()}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    // Renderizado normal con ServiceArea cuando no hay fixedServiceArea
                    <ServiceArea
                      title={'Service area'}
                      isEditing={isEditingAllowed}
                      canEdit={false}
                      googleProfileInfo={
                        googleProfileInfo.data?.serviceArea?.places?.placeInfos
                      }
                      onFieldChange={(value) =>
                        handleFieldChange('serviceArea', value)
                      }>
                      {googleProfileInfo.data?.serviceArea?.places?.placeInfos
                        ? googleProfileInfo.data?.serviceArea?.places?.placeInfos.map(
                            (place, index) => (
                              <span key={index}>
                                {place.placeName}
                                <br />
                              </span>
                            )
                          )
                        : '—'}
                    </ServiceArea>
                  )}
                  {loadingAgent && (
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="flex items-center"
                        style={{ minHeight: 60 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Analyzing service area...</span>
                      </div>
                    </div>
                  )}
                  {showAgentNotifications && (
                    <div style={{ marginTop: 8 }}>
                      {qualityAgentData &&
                      qualityAgentData.recomendations?.serviceArea?.places
                        ?.placeInfos
                        ? (() => {
                            // Obtener datos actuales del service area
                            const currentServiceAreas =
                              googleProfileInfo.data?.serviceArea?.places
                                ?.placeInfos || [];

                            // Si ya tiene 12 o más, mostrar notificación verde
                            if (currentServiceAreas.length >= 12) {
                              if (!dismissedNotifications.serviceArea_max) {
                                return (
                                  <QualityAgentNotification
                                    status="good"
                                    message="Service areas are complete."
                                    onDismiss={() =>
                                      handleDismissNotification(
                                        'serviceArea_max'
                                      )
                                    }
                                    loadingDismiss={
                                      loadingDismiss.serviceArea_max
                                    }
                                  />
                                );
                              }
                              return null;
                            }

                            // Si tiene menos de 12, mostrar recomendaciones individuales
                            if (
                              recommendedServiceAreas &&
                              recommendedServiceAreas.length > 0
                            ) {
                              return recommendedServiceAreas.map((place) => {
                                const notificationKey = `recommended_service_area_${place.id}`;
                                if (dismissedNotifications[notificationKey]) {
                                  return null;
                                }

                                return (
                                  <div
                                    key={place.id}
                                    style={{ marginBottom: 8 }}>
                                    <QualityAgentNotification
                                      status="recommended"
                                      message={`Add ${place.placeName} to your service area`}
                                      onFix={async () => {
                                        await handleFixRecommendedServiceArea(
                                          place.id,
                                          place.placeName
                                        );
                                      }}
                                      onDismiss={() =>
                                        handleDismissNotification(
                                          notificationKey
                                        )
                                      }
                                      suggestion={`Expanding your service area to ${place.placeName} will help customers in this location find your business.`}
                                      loadingFix={
                                        loadingFix[
                                          `recommended_service_area_${place.id}`
                                        ]
                                      }
                                      loadingDismiss={
                                        loadingDismiss[
                                          `recommended_service_area_${place.id}`
                                        ]
                                      }
                                      fixButtonText="Add"
                                      isAnyOperationInProgress={
                                        isAnyOperationInProgress
                                      }
                                    />
                                  </div>
                                );
                              });
                            }

                            return null;
                          })()
                        : !dismissedNotifications.serviceArea && (
                            <QualityAgentNotification
                              status={qualityAgentMock.serviceArea.status}
                              message={qualityAgentMock.serviceArea.message}
                              onDismiss={() =>
                                handleDismissNotification('serviceArea')
                              }
                              suggestion={randomAgentSuggestions.serviceArea}
                              loadingDismiss={loadingDismiss.serviceArea}
                            />
                          )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Services List */}
              <Card
                id="services"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Settings className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-xl lg:text-2xl font-bold">
                          Services List
                        </CardTitle>
                        <CardDescription className="text-base mt-1">
                          The services you offer to your customers
                        </CardDescription>
                      </div>
                    </div>

                    <div className="w-full lg:ml-auto lg:max-w-[720px]">
                      {showQualityAgent &&
                        !loadingAgent &&
                        qualityAgentData &&
                        (() => {
                          const servicesData = findServicesField(
                            googleProfileInfo.data
                          );
                          const currentServices =
                            processServicesList(servicesData) || [];

                          let recommendedServicesList = [];
                          if (
                            qualityAgentData?.recomendations?.serviceItems
                              ?.recommendedServices?.message
                          ) {
                            try {
                              recommendedServicesList =
                                parseRecommendedServices(
                                  qualityAgentData.recomendations.serviceItems
                                    .recommendedServices.message
                                );
                            } catch (error) {
                              console.error(
                                'Error parsing recommended services:',
                                error
                              );
                            }
                          }

                          let allServices =
                            fixedServicesList !== null
                              ? [...fixedServicesList]
                              : [...currentServices];

                          if (recommendedServicesList.length > 0) {
                            recommendedServicesList.forEach(
                              (recommendedService) => {
                                const alreadyExists = allServices.some(
                                  (existingService) =>
                                    existingService.name ===
                                    recommendedService.name
                                );

                                if (!alreadyExists) {
                                  allServices.push(recommendedService);
                                }
                              }
                            );
                          }

                          const servicesWithoutDescription = allServices.filter(
                            (service) =>
                              !service.description ||
                              service.description.trim() === ''
                          );

                          const hasServicesWithoutDescription =
                            servicesWithoutDescription.length > 0;

                          if (
                            !hasServicesWithoutDescription ||
                            dismissedNotifications['services_general']
                          ) {
                            return null;
                          }

                          return (
                            <QualityAgentNotification
                              status="bad"
                              message={`${servicesWithoutDescription.length} of your services are missing descriptions.`}
                              onFix={() =>
                                handleFixField(
                                  'serviceItems.descriptionKeywordAnalysis'
                                )
                              }
                              onDismiss={() =>
                                handleDismissNotification('services_general')
                              }
                              suggestion={
                                qualityAgentData?.recomendations?.serviceItems
                                  ?.descriptionKeywordAnalysis?.message
                                  ? 'Click Fix All to automatically add optimized descriptions to your services based on AI recommendations.'
                                  : 'Add detailed descriptions to all your services to improve visibility and attract more customers.'
                              }
                              loadingFix={loadingFix['services_general']}
                              loadingDismiss={
                                loadingDismiss['services_general']
                              }
                              fixButtonText="Fix All"
                              isAnyOperationInProgress={
                                isAnyOperationInProgress
                              }
                            />
                          );
                        })()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {(() => {
                    // Buscar el campo de servicios automáticamente
                    const servicesData = findServicesField(
                      googleProfileInfo.data
                    );

                    const currentServices = processServicesList(servicesData);

                    // Obtener servicios recomendados si están disponibles
                    let recommendedServicesList = [];
                    if (
                      qualityAgentData?.recomendations?.serviceItems
                        ?.recommendedServices?.message
                    ) {
                      try {
                        recommendedServicesList = parseRecommendedServices(
                          qualityAgentData.recomendations.serviceItems
                            .recommendedServices.message
                        );
                        // No llamar setRecommendedServices aquí para evitar re-renders infinitos
                      } catch (error) {
                        console.error(
                          'Error parsing recommended services:',
                          error
                        );
                      }
                    }

                    // Combinar servicios existentes con servicios recomendados
                    let allServices = [];

                    // Si hay servicios fijos, usar esos
                    if (fixedServicesList !== null) {
                      allServices = [...fixedServicesList];
                    } else if (currentServices) {
                      allServices = [...currentServices];
                    }

                    // Agregar servicios recomendados que no estén ya en la lista
                    if (recommendedServicesList.length > 0) {
                      recommendedServicesList.forEach((recommendedService) => {
                        const alreadyExists = allServices.some(
                          (existingService) =>
                            existingService.name === recommendedService.name
                        );
                        if (!alreadyExists) {
                          allServices.push(recommendedService);
                        }
                      });
                    }

                    return renderServicesList(servicesData, allServices);
                  })()}
                  {loadingAgent && (
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="flex items-center"
                        style={{ minHeight: 60 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>
                          Analyzing services and generating recommendations...
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Review Frequency */}
              <Card
                id="review-frequency"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Review Frequency
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        How often customers leave reviews for your business
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-muted-foreground" />
                      Current frequency
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Your current review rate compared to the recommended rate
                    </p>
                    <p className="text-base mt-2">
                      {googleProfileInfo.data?.reviews_info?.reviewFrecuency
                        ? googleProfileInfo.data?.reviews_info?.reviewFrecuency
                        : '—'}
                    </p>
                  </div>
                  {loadingAgent && (
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="flex items-center"
                        style={{ minHeight: 60 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">
                          Analyzing review frequency...
                        </span>
                      </div>
                    </div>
                  )}
                  {showAgentNotifications &&
                    !dismissedNotifications.reviewFrequency && (
                      <div style={{ marginTop: 8 }}>
                        {(() => {
                          // Lógica condicional basada en review frequency > 1
                          const reviewFreqValue = parseFloat(
                            googleProfileInfo.data?.reviews_info
                              ?.reviewFrecuency
                          );
                          const isReviewFreqGood = reviewFreqValue > 1;

                          const mockStatus = isReviewFreqGood ? 'good' : 'bad';
                          const mockMessage = isReviewFreqGood
                            ? 'The review frequency is OK.'
                            : 'Review Frequency should be more than 1 review per week.';
                          const mockSuggestion = isReviewFreqGood
                            ? 'Maintain this review frequency to improve your visibility.'
                            : 'Encourage your clients to leave reviews to increase frequency.';

                          return (
                            <QualityAgentNotification
                              status={mockStatus}
                              message={mockMessage}
                              onDismiss={() =>
                                handleDismissNotification('reviewFrequency')
                              }
                              suggestion={mockSuggestion}
                              loadingFix={loadingFix.reviewFrequency}
                              loadingDismiss={loadingDismiss.reviewFrequency}
                            />
                          );
                        })()}
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Google Rating */}
              <Card
                id="google-rating"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-yellow-100 flex items-center justify-center">
                      <Star className="h-6 w-6 text-yellow-600 fill-yellow-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Google Rating
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Your overall star rating on Google
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <Star className="h-5 w-5 text-muted-foreground" />
                      Current rating
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Your average star rating based on customer reviews
                    </p>
                    {googleProfileInfo.data?.reviews_info?.averageRating ? (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-2xl font-bold">
                          {googleProfileInfo.data?.reviews_info?.averageRating.toFixed(
                            1
                          )}
                        </span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`h-6 w-6 ${
                                i <
                                Math.floor(
                                  googleProfileInfo.data?.reviews_info
                                    ?.averageRating
                                )
                                  ? 'text-yellow-500 fill-yellow-500'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-2xl font-bold">—</span>
                      </div>
                    )}
                  </div>
                  {loadingAgent && (
                    <div style={{ marginTop: 8 }}>
                      <div
                        className="flex items-center"
                        style={{ minHeight: 60 }}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">
                          Analyzing Google rating...
                        </span>
                      </div>
                    </div>
                  )}
                  {showAgentNotifications &&
                    !dismissedNotifications.googleRating && (
                      <div style={{ marginTop: 8 }}>
                        {(() => {
                          // Lógica condicional basada en Google Rating > 4
                          const ratingValue = parseFloat(
                            googleProfileInfo.data?.reviews_info?.averageRating
                          );
                          const isRatingGood = ratingValue > 4;

                          const mockStatus = isRatingGood ? 'good' : 'bad';
                          const mockMessage = isRatingGood
                            ? 'The Google rating is OK.'
                            : 'Google Rating should be greater than 4.';
                          const mockSuggestion = isRatingGood
                            ? 'Maintain this rating to build trust with potential clients.'
                            : 'Ask satisfied clients to leave positive reviews to improve your rating.';

                          return (
                            <QualityAgentNotification
                              status={mockStatus}
                              message={mockMessage}
                              onDismiss={() =>
                                handleDismissNotification('googleRating')
                              }
                              suggestion={mockSuggestion}
                              loadingFix={loadingFix.googleRating}
                              loadingDismiss={loadingDismiss.googleRating}
                            />
                          );
                        })()}
                      </div>
                    )}
                </CardContent>
              </Card>

              {/* Profile Quality */}
              <Card
                id="quality"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Star className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Profile Quality
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        Metrics and statistics about your profile performance
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {/* Grid de 2 columnas para los campos */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6">
                    {/* Columna 1 */}
                    <div className="space-y-6">
                      {/* Review Replies */}
                      <div>
                        <InfoField
                          title={'Review Replies'}
                          isEditing={isEditingAllowed}
                          canEdit={!GBPfields.reviewReplies}
                          hasChanges={!!editedFields.reviewReplies}
                          onFieldChange={(value) =>
                            handleFieldChange('reviewReplies', value)
                          }>
                          {googleProfileInfo.data?.reviews_info?.reviewsReplied
                            ? String(
                                googleProfileInfo.data?.reviews_info
                                  ?.reviewsReplied
                              )
                            : '—'}
                        </InfoField>
                        {loadingAgent && (
                          <div style={{ marginTop: 8 }}>
                            <div
                              className="flex items-center"
                              style={{ minHeight: 60 }}>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">
                                Analyzing review replies...
                              </span>
                            </div>
                          </div>
                        )}
                        {showAgentNotifications &&
                          !dismissedNotifications.reviewReplies && (
                            <div style={{ marginTop: 8 }}>
                              {(() => {
                                const repliesValue = parseFloat(
                                  googleProfileInfo.data?.reviews_info
                                    ?.reviewsReplied
                                );
                                const isRepliesGood = repliesValue > 1;

                                const mockStatus = isRepliesGood
                                  ? 'good'
                                  : 'bad';
                                const mockMessage = isRepliesGood
                                  ? 'The review replies are OK.'
                                  : 'Review Replies should more than 1 reply per week.';
                                const mockSuggestion = isRepliesGood
                                  ? 'Keep responding to reviews to maintain good engagement.'
                                  : 'Make sure to reply to all reviews to show customer care.';

                                return (
                                  <QualityAgentNotification
                                    status={mockStatus}
                                    message={mockMessage}
                                    onDismiss={() =>
                                      handleDismissNotification('reviewReplies')
                                    }
                                    suggestion={mockSuggestion}
                                    loadingDismiss={
                                      loadingDismiss.reviewReplies
                                    }
                                  />
                                );
                              })()}
                            </div>
                          )}
                      </div>

                      {/* Post Frequency */}
                      <div>
                        <InfoField
                          title={'Post Frequency'}
                          isEditing={isEditingAllowed}
                          canEdit={!GBPfields.postFrequency}
                          hasChanges={!!editedFields.postFrequency}
                          onFieldChange={(value) =>
                            handleFieldChange('postFrequency', value)
                          }>
                          {googleProfileInfo.data?.posts_info?.posts_frecuency
                            ? googleProfileInfo.data?.posts_info
                                ?.posts_frecuency
                            : '—'}
                        </InfoField>
                        {loadingAgent && (
                          <div style={{ marginTop: 8 }}>
                            <div
                              className="flex items-center"
                              style={{ minHeight: 60 }}>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">
                                Analyzing post frequency...
                              </span>
                            </div>
                          </div>
                        )}
                        {showAgentNotifications &&
                          !dismissedNotifications.postFrequency && (
                            <div style={{ marginTop: 8 }}>
                              {(() => {
                                const postFreqValue = parseFloat(
                                  googleProfileInfo.data?.posts_info
                                    ?.posts_frecuency
                                );
                                const isPostFreqGood = postFreqValue > 3;

                                const mockStatus = isPostFreqGood
                                  ? 'good'
                                  : 'bad';
                                const mockMessage = isPostFreqGood
                                  ? 'The post frequency is OK.'
                                  : 'Post Frequency should be greater than 3 posts per week.';
                                const mockSuggestion = isPostFreqGood
                                  ? 'Keep maintaining this posting frequency to stay engaged with your audience.'
                                  : 'Increase your posting frequency to keep your audience engaged.';

                                return (
                                  <QualityAgentNotification
                                    status={mockStatus}
                                    message={mockMessage}
                                    onDismiss={() =>
                                      handleDismissNotification('postFrequency')
                                    }
                                    suggestion={mockSuggestion}
                                    loadingFix={loadingFix.postFrequency}
                                    loadingDismiss={
                                      loadingDismiss.postFrequency
                                    }
                                  />
                                );
                              })()}
                            </div>
                          )}
                      </div>

                      {/* Opening date */}
                      <div>
                        <OpeningDateField
                          title={'Opening date'}
                          isEditing={isEditingAllowed}
                          canEdit={!GBPfields.openingDate}
                          openingDate={
                            googleProfileInfo.data?.openInfo?.openingDate
                          }
                          onFieldChange={(value) =>
                            handleFieldChange('openingDate', value)
                          }>
                          {googleProfileInfo.data?.openInfo?.openingDate
                            ? `${googleProfileInfo.data.openInfo.openingDate.year}/${googleProfileInfo.data.openInfo.openingDate.month}/${googleProfileInfo.data.openInfo.openingDate.day}`
                            : '—'}
                        </OpeningDateField>
                      </div>
                    </div>

                    {/* Columna 2 */}
                    <div className="space-y-6">
                      {/* Number Of Images */}
                      <div>
                        <InfoField
                          title={'Number Of Images'}
                          isEditing={isEditingAllowed}
                          canEdit={!GBPfields.numberOfImages}
                          hasChanges={!!editedFields.numberOfImages}
                          onFieldChange={(value) =>
                            handleFieldChange('numberOfImages', value)
                          }>
                          {googleProfileInfo.data?.images_info
                            ?.totalMediaItemCount !== undefined
                            ? String(
                                googleProfileInfo.data.images_info
                                  .totalMediaItemCount
                              )
                            : '—'}
                        </InfoField>
                        {loadingAgent && (
                          <div style={{ marginTop: 8 }}>
                            <div
                              className="flex items-center"
                              style={{ minHeight: 60 }}>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">
                                Analyzing number of images...
                              </span>
                            </div>
                          </div>
                        )}
                        {showAgentNotifications &&
                          !dismissedNotifications.numberOfImages && (
                            <div style={{ marginTop: 8 }}>
                              {(() => {
                                const imagesValue = parseFloat(
                                  googleProfileInfo.data?.images_info
                                    ?.totalMediaItemCount
                                );
                                const isImagesGood = imagesValue >= 3;

                                const mockStatus = isImagesGood
                                  ? 'good'
                                  : 'bad';
                                const mockMessage = isImagesGood
                                  ? 'The number of images is OK.'
                                  : 'Number Of Images should be greater than 3 images per week.';
                                const mockSuggestion = isImagesGood
                                  ? 'Keep adding high-quality images to maintain visual appeal.'
                                  : 'Add more high-quality images to attract potential clients.';

                                return (
                                  <QualityAgentNotification
                                    status={mockStatus}
                                    message={mockMessage}
                                    onDismiss={() =>
                                      handleDismissNotification(
                                        'numberOfImages'
                                      )
                                    }
                                    suggestion={mockSuggestion}
                                    loadingDismiss={
                                      loadingDismiss.numberOfImages
                                    }
                                  />
                                );
                              })()}
                            </div>
                          )}
                      </div>

                      {/* Post Keyword Optimization */}
                      <div>
                        <InfoField
                          title={'Post Keyword Optimization'}
                          isEditing={isEditingAllowed}
                          canEdit={!GBPfields.postKeywordOptimization}
                          hasChanges={!!editedFields.postKeywordOptimization}
                          onFieldChange={(value) =>
                            handleFieldChange('postKeywordOptimization', value)
                          }>
                          {visualPostsKeywords || '—'}
                        </InfoField>
                        {loadingAgent && (
                          <div style={{ marginTop: 8 }}>
                            <div
                              className="flex items-center"
                              style={{ minHeight: 60 }}>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-sm">
                                Analyzing keyword optimization...
                              </span>
                            </div>
                          </div>
                        )}
                        {showAgentNotifications &&
                          !dismissedNotifications.postKeywordOptimization && (
                            <div style={{ marginTop: 8 }}>
                              {qualityAgentData?.recomendations?.posts_info
                                ?.posts_keywords?.message &&
                                (() => {
                                  const currentPostKeywordOptimization =
                                    visualPostsKeywords || '';
                                  const apiRecommendation =
                                    qualityAgentData.recomendations?.posts_info
                                      ?.posts_keywords?.message;
                                  const isRecommendationDifferent =
                                    apiRecommendation &&
                                    JSON.stringify(
                                      currentPostKeywordOptimization
                                    ) !== JSON.stringify(apiRecommendation);
                                  const status = isRecommendationDifferent
                                    ? 'bad'
                                    : 'good';
                                  const message = isRecommendationDifferent
                                    ? 'There is a post keyword optimization recommendation.'
                                    : 'Your post keyword optimization is good.';

                                  return (
                                    <QualityAgentNotification
                                      status={status}
                                      message={message}
                                      onFix={() =>
                                        handleFixField(
                                          'posts_info.posts_keywords'
                                        )
                                      }
                                      onDismiss={() =>
                                        handleDismissNotification(
                                          'postKeywordOptimization'
                                        )
                                      }
                                      suggestion={apiRecommendation}
                                      loadingFix={
                                        loadingFix.postKeywordOptimization
                                      }
                                      loadingDismiss={
                                        loadingDismiss.postKeywordOptimization
                                      }
                                      isAnyOperationInProgress={
                                        isAnyOperationInProgress
                                      }
                                    />
                                  );
                                })()}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Information */}
              <Card
                id="contact"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Phone className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Contact Information
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        How customers can reach your business
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  <PhoneField
                    title={
                      <span className="flex items-center gap-2">
                        <Phone className="h-5 w-5 text-muted-foreground" />
                        Phone number
                      </span>
                    }
                    description="Your primary business phone number"
                    isEditing={isEditingAllowed}
                    canEdit={false}
                    googleProfileInfo={googleProfileInfo.data?.phoneNumbers}
                    onFieldChange={(value) =>
                      handleFieldChange('phoneNumber', value)
                    }>
                    {googleProfileInfo.data?.phoneNumbers ? (
                      <div>
                        {googleProfileInfo.data?.phoneNumbers?.primaryPhone}{' '}
                        <span className="badge badge-secondary">PRIMARY</span>
                        <br />
                        {googleProfileInfo.data?.phoneNumbers?.additionalPhones?.map(
                          (phone, index) => {
                            return (
                              <span key={index}>
                                {phone}
                                <br />
                              </span>
                            );
                          }
                        )}
                      </div>
                    ) : (
                      '—'
                    )}
                  </PhoneField>

                  <InfoField
                    title={
                      <span className="flex items-center gap-2">
                        <Globe className="h-5 w-5 text-muted-foreground" />
                        Website
                      </span>
                    }
                    description="Your business website URL"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields.website}
                    hasChanges={!!editedFields.website}
                    onFieldChange={(value) =>
                      handleFieldChange('website', value)
                    }>
                    {googleProfileInfo.data?.websiteUri
                      ? googleProfileInfo.data?.websiteUri
                      : '—'}
                  </InfoField>

                  <InfoField
                    title={
                      <span className="flex items-center gap-2">
                        <Link2 className="h-5 w-5 text-muted-foreground" />
                        Short name
                      </span>
                    }
                    description="Your custom Google Maps short URL"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields.shortName}
                    onFieldChange={(value) =>
                      handleFieldChange('shortName', value)
                    }>
                    {googleProfileInfo.data?.title
                      ? googleProfileInfo.data?.title.toLowerCase()
                      : '—'}
                  </InfoField>

                  <InfoField
                    title={
                      <span className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-muted-foreground" />
                        Social profiles
                      </span>
                    }
                    description="Your connected social media accounts"
                    isEditing={isEditingAllowed}
                    canEdit={!GBPfields.socialProfiles}
                    onFieldChange={(value) =>
                      handleFieldChange('socialProfiles', value)
                    }>
                    {googleProfileInfo.data?.description
                      ? googleProfileInfo.data?.description
                      : '—'}
                  </InfoField>
                </CardContent>
              </Card>

              {/* Opening hours */}
              <Card
                id="hours"
                className="shadow-md rounded-2xl border-0 scroll-mt-[180px]">
                <CardHeader className="pb-3 sm:pb-4 pt-4 sm:pt-6 px-4 sm:px-6 lg:px-8">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Clock className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-xl lg:text-2xl font-bold">
                        Opening Hours
                      </CardTitle>
                      <CardDescription className="text-base mt-1">
                        When your business is open for customers
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6 lg:px-8 pb-6 sm:pb-8">
                  {/* Regular hours */}
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      Regular hours
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Your standard business hours for each day
                    </p>
                  </div>

                  {googleProfileInfo.data?.regularHours?.periods ? (
                    <div className="space-y-2 mt-3">
                      {googleProfileInfo.data?.regularHours?.periods.map(
                        (period, index) => (
                          <div
                            key={index}
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 py-3 px-4 bg-muted/30 rounded-lg">
                            <span className="font-medium text-base">
                              {period.openDay.charAt(0).toUpperCase() +
                                period.openDay.toLowerCase().slice(1)}
                            </span>
                            <Badge variant="secondary" className="text-sm">
                              {period.openTime.hours}:00 -{' '}
                              {period.closeTime.hours}:00
                            </Badge>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <p className="text-base mt-2">—</p>
                  )}

                  {/* Special hours */}
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      Special Hours
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Holidays or special occasion hours
                    </p>
                    {googleProfileInfo.data?.specialHours ? (
                      <div className="mt-2">
                        <SpecialHours
                          specialHourPeriods={
                            googleProfileInfo.data.specialHours
                              .specialHourPeriods
                          }
                        />
                      </div>
                    ) : (
                      <p className="text-base mt-2">—</p>
                    )}
                  </div>

                  {/* More hours */}
                  <div className="space-y-2">
                    <p className="text-sm sm:text-base font-semibold flex items-center gap-2">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                      More Hours
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Additional hours information
                    </p>
                    <p className="text-base mt-2">
                      {formatMoreHours(googleProfileInfo.data?.moreHours)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    );
  }
);

GoogleBusinessProfile.propTypes = {
  isEditing: PropTypes.bool,
  setIsEditing: PropTypes.func,
  showQualityAgent: PropTypes.bool,
  setShowQualityAgent: PropTypes.func,
  loadingAgent: PropTypes.bool,
  setLoadingAgent: PropTypes.func,
  handleShowQualityAgentRef: PropTypes.object,
  onOptimizationChange: PropTypes.func,
  onCriteriaUpdate: PropTypes.func,
  onChangesUpdate: PropTypes.func,
  currSection: PropTypes.string,
  setCurrSection: PropTypes.func,
  hasProPlan: PropTypes.bool
};

GoogleBusinessProfile.defaultProps = {
  isEditing: false,
  hasProPlan: true
};

export default GoogleBusinessProfile;
